import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { toast } from "sonner";
import { LayoutDashboard, ShoppingCart, Truck, Users, Package as PackIcon, Gift, Crown, Ticket, FileText, Settings as SettingsIcon, Sparkles, LifeBuoy, Star, Trash2, RefreshCw, Megaphone, Mail, RotateCcw } from "lucide-react";
import Accounting from "./Accounting";

const TABS = [
  ["dashboard","Dashboard",<LayoutDashboard className="w-4 h-4"/>],
  ["orders","Orders",<ShoppingCart className="w-4 h-4"/>],
  ["fulfilment","Fulfilment",<Truck className="w-4 h-4"/>],
  ["reconciliation","Reconciliation",<Ticket className="w-4 h-4"/>],
  ["accounting","Accounting",<FileText className="w-4 h-4"/>],
  ["customers","Customers",<Users className="w-4 h-4"/>],
  ["referrals","Referrals",<Users className="w-4 h-4"/>],
  ["catalogue","Catalogue",<PackIcon className="w-4 h-4"/>],
  ["offers","Vault Offers",<Sparkles className="w-4 h-4"/>],
  ["events","Event Board",<Megaphone className="w-4 h-4"/>],
  ["promo","Promo Codes",<Ticket className="w-4 h-4"/>],
  ["support","Support",<LifeBuoy className="w-4 h-4"/>],
  ["reviews","Reviews",<Star className="w-4 h-4"/>],
  ["loyalty","Loyalty",<Crown className="w-4 h-4"/>],
  ["emails","Emails",<Mail className="w-4 h-4"/>],
  ["settings","Settings",<SettingsIcon className="w-4 h-4"/>],
  ["audit","Audit Log",<FileText className="w-4 h-4"/>],
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [refreshTick, setRefreshTick] = useState(0);
  const [spinning, setSpinning] = useState(false);
  if (!user || !["owner","admin","fulfilment","support","finance"].includes(user.role)) return <div><Header/><div className="pt-32 text-center text-[var(--av-text-dim)]">Admin access required.</div></div>;

  const doRefresh = () => {
    setSpinning(true);
    setRefreshTick(t => t + 1);
    // Spinner runs long enough for the eye — actual refetch is instant.
    setTimeout(() => setSpinning(false), 550);
  };

  return (
    <div>
      <Header/>
      <div className="max-w-7xl mx-auto pt-24 sm:pt-28 pb-20 px-3 sm:px-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="mono-caption">Arthur's Vault Admin</div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold gold-text mt-2 mb-4 sm:mb-6">Command Center</h1>
          </div>
          <button
            data-testid="admin-refresh-btn"
            onClick={doRefresh}
            disabled={spinning}
            title="Refresh the current tab"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--av-gold)]/50 text-[var(--av-gold)] hover:bg-[rgba(212,175,55,0.06)] transition text-xs sm:text-sm disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`} />
            {spinning ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="-mx-3 sm:mx-0 mb-6 sm:mb-8">
          {/* Mobile: full tab picker via native select — every section reachable in one tap */}
          <div className="sm:hidden px-3">
            <label htmlFor="admin-tab-select" className="mono-caption block mb-2">Section</label>
            <div className="relative">
              <select
                id="admin-tab-select"
                data-testid="admin-tab-select"
                value={tab}
                onChange={(e) => setTab(e.target.value)}
                className="w-full appearance-none rounded-xl border border-[var(--av-border)] bg-[rgba(11,12,16,0.85)] px-4 py-3 pr-10 text-sm font-medium text-[var(--av-gold)] focus:outline-none focus:ring-1 focus:ring-[var(--av-gold)]"
              >
                {TABS.map(([id, label]) => (
                  <option key={id} value={id} className="bg-[#12141c] text-[var(--av-text)]">{label}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--av-gold)]">▾</span>
            </div>
          </div>
          {/* Desktop / tablet: existing scrollable tab strip */}
          <div className="hidden sm:block overflow-x-auto no-scrollbar">
            <div className="flex gap-2 sm:flex-wrap">
              {TABS.map(([id,label,icon])=>(<button key={id} data-testid={`admin-tab-${id}`} onClick={()=>setTab(id)} className={`tab-btn flex items-center gap-2 whitespace-nowrap ${tab===id?"active":""}`}>{icon}{label}</button>))}
            </div>
          </div>
        </div>
        {/* Key remount pattern — bumping refreshTick unmounts the current
            tab and remounts it, which fires every child useEffect and
            refetches every /api/admin/* payload without touching each
            individual tab component. */}
        <div key={refreshTick}>
          {tab==="dashboard" && <Dashboard/>}
          {tab==="orders" && <Orders/>}
          {tab==="fulfilment" && <Fulfilment/>}
          {tab==="reconciliation" && <Reconciliation/>}
          {tab==="accounting" && <Accounting/>}
          {tab==="customers" && <Customers/>}
          {tab==="referrals" && <ReferralsAdmin/>}
          {tab==="catalogue" && <Catalogue/>}
          {tab==="offers" && <Offers/>}
          {tab==="events" && <EventBoardAdmin/>}
          {tab==="promo" && <PromoCodes/>}
          {tab==="support" && <SupportTickets/>}
          {tab==="reviews" && <ReviewsAdmin/>}
          {tab==="loyalty" && <LoyaltyAdmin/>}
          {tab==="emails" && <EmailsAdmin/>}
          {tab==="settings" && <Settings/>}
          {tab==="audit" && <Audit/>}
        </div>
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="vault-card p-4 sm:p-6 min-w-0">
    {title && <div className="mono-caption mb-3">{title}</div>}
    <div className="-mx-4 sm:mx-0 overflow-x-auto">
      <div className="px-4 sm:px-0 min-w-[560px] sm:min-w-0">{children}</div>
    </div>
  </div>
);

function SalesPauseControl() {
  // Owner-only prominent Pause/Resume Sales control. Reads and writes the
  // same `store_status` field the Settings tab exposes, but surfaces it at
  // the top of the Dashboard so pausing is one click away in an incident.
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then(r => setStatus(r.data.store_status || "open"));
  }, []);
  const paused = status === "maintenance" || status === "closed";
  const toggle = async () => {
    const next = paused ? "open" : "closed";
    if (!window.confirm(next === "closed"
      ? "Pause ALL sales? Every Buy / Claim / Checkout button will be disabled site-wide and the server will reject any bypass attempts."
      : "Resume sales? Customers will be able to purchase packs and special offers again immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.get("/admin/settings");
      await api.put("/admin/settings", { ...data, store_status: next });
      setStatus(next);
      // Force the site-wide status hook to refresh so every customer's
      // buttons update within a second instead of the 30s poll cycle.
      const { refreshStoreStatus } = await import("../lib/storeStatus");
      refreshStoreStatus();
      toast.success(next === "closed" ? "Sales paused site-wide" : "Sales resumed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update store status");
    } finally { setBusy(false); }
  };
  if (status === null) return null;
  return (
    <div
      data-testid="sales-pause-control"
      className={`rounded-xl border-2 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
        paused
          ? "border-red-500/70 bg-red-500/10"
          : "border-emerald-500/50 bg-emerald-500/5"}`}
    >
      <div className="min-w-0">
        <div className={`mono-caption mb-1 ${paused ? "text-red-300" : "text-emerald-300"}`}>
          {paused ? "🔴 SALES PAUSED" : "🟢 SALES LIVE"}
        </div>
        <div className="text-sm text-[var(--av-text)] font-semibold">
          {paused
            ? "Every Buy, Claim and Checkout button is disabled site-wide."
            : "Customers can purchase packs and special offers."}
        </div>
        <div className="text-[11px] text-[var(--av-text-dim)] mt-0.5">
          Toggle also available in Settings → Business Settings under a wider set of statuses.
        </div>
      </div>
      <button
        data-testid="sales-pause-toggle"
        onClick={toggle}
        disabled={busy}
        className={`shrink-0 px-5 py-3 rounded-lg font-serif font-bold uppercase tracking-wider text-sm transition disabled:opacity-40 ${
          paused
            ? "bg-emerald-500 text-black hover:bg-emerald-400"
            : "bg-red-600 text-white hover:bg-red-500"}`}
      >
        {busy ? "…" : (paused ? "Resume Sales" : "Pause All Sales")}
      </button>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [daily, setDaily] = useState(null);
  const [popularity, setPopularity] = useState(null);
  const [popularityWindow, setPopularityWindow] = useState("all");
  useEffect(()=>{
    api.get("/admin/dashboard").then(r=>setD(r.data));
    api.get("/admin/dashboard/daily-profit").then(r=>setDaily(r.data)).catch(()=>setDaily({days:[], totals:{}}));
  },[]);
  // Refetch popularity whenever window changes, and refresh every 60s so
  // owner sees intent shifts in near-real-time without manual reload.
  useEffect(()=>{
    let cancelled = false;
    const load = () => api.get(`/admin/dashboard/product-popularity?window=${popularityWindow}`)
      .then(r => { if (!cancelled) setPopularity(r.data); })
      .catch(() => { if (!cancelled) setPopularity({rows:[], totals:{}}); });
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  },[popularityWindow]);
  if (!d) return <div className="py-10 text-center text-[var(--av-text-dim)] text-sm">Loading dashboard…</div>;
  const stat = (label, val, color="gold", testid=null) => (
    <Card title={label}>
      <div data-testid={testid || undefined} className={`font-serif text-3xl font-bold ${color==="gold"?"gold-text":"purple-text"}`}>{val}</div>
    </Card>
  );
  const fmt = n => `£${(Number(n)||0).toFixed(2)}`;
  return (
    <div className="space-y-6">
      <SalesPauseControl/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stat("Revenue Today", fmt(d.revenue_today))}
        {typeof d.revenue_all_time === "number" && stat("Revenue (All Time)", fmt(d.revenue_all_time), "purple", "stat-revenue-all-time")}
        {stat("Profit Today", fmt(d.profit_today_gbp), "gold", "stat-profit-today")}
        {stat("Profit (All Time)", fmt(d.profit_all_time_gbp), "purple", "stat-profit-all-time")}
        {stat("Orders Today", d.orders_today)}
        {stat("Apex Sold Today", d.apex_sold_today.toLocaleString())}
        {stat("Paid Waiting", d.paid_waiting, "purple")}
        {stat("Processing", d.processing, "purple")}
        {stat("Customer Issues", d.issues, "purple")}
        {stat("New Customers", d.new_customers)}
        {stat("Rewards Issued", fmt(d.rewards_issued))}
        {stat("Rewards Redeemed", fmt(d.rewards_redeemed), "purple")}
        {stat("Card Orders", d.card_orders)}
        {stat("Pay by Bank", d.pay_by_bank_orders, "purple")}
        {stat("Completed Today", d.completed_today)}
        {typeof d.orders_last_hour === "number" && stat("Orders (last hour)", d.orders_last_hour, "gold", "stat-orders-last-hour")}
      </div>
      <DailyProfitBreakdown daily={daily} />
      <ProductPopularity popularity={popularity} window={popularityWindow} setWindow={setPopularityWindow} />
    </div>
  );
}

/**
 * Pack popularity + intent-vs-conversion, grouped by pack size.
 * Level 1: each pack size (499 / 999 / 1999 / 4999 / 9999) — shows total
 * conversion for the whole pack across all quantities.
 * Level 2 (inside each pack card): every quantity customers actually
 * ordered, e.g. "1 pack", "100 packs" — sorted by paid orders desc so
 * the most popular quantity for that pack always tops its own list.
 * Refetches every 60s.
 */
function ProductPopularity({ popularity, window, setWindow }) {
  if (!popularity) return (
    <Card title="Product Popularity — customer intent"><div className="text-sm text-[var(--av-text-dim)]">Loading…</div></Card>
  );
  const rows = popularity.rows || [];
  const totals = popularity.totals || {};
  const fmt = n => `£${(Number(n)||0).toFixed(2)}`;
  const pct = n => `${((Number(n)||0)*100).toFixed(1)}%`;
  const windowLabel = { all: "All-time", "30d": "Last 30 days", "7d": "Last 7 days" }[window] || "All-time";
  // Group by pack size (apex_amount). Build a summary per pack across every quantity.
  const groups = {};
  for (const r of rows) {
    const key = r.apex_amount;
    if (!groups[key]) groups[key] = {
      apex_amount: key,
      variants: [],
      attempted_orders: 0, paid_orders: 0, abandoned_orders: 0,
      packs_sold: 0, revenue: 0, profit: 0,
    };
    const g = groups[key];
    g.variants.push(r);
    g.attempted_orders += r.attempted_orders;
    g.paid_orders += r.paid_orders;
    g.abandoned_orders += r.abandoned_orders;
    g.packs_sold += r.packs_sold;
    g.revenue += Number(r.revenue) || 0;
    g.profit += Number(r.profit) || 0;
  }
  for (const g of Object.values(groups)) {
    g.conversion_rate = g.attempted_orders ? g.paid_orders / g.attempted_orders : 0;
    g.variants.sort((a, b) => b.paid_orders - a.paid_orders || b.attempted_orders - a.attempted_orders);
  }
  // Pack groups sorted by total paid orders desc (bestselling pack first).
  const groupList = Object.values(groups).sort((a, b) =>
    b.paid_orders - a.paid_orders || b.revenue - a.revenue);
  return (
    <Card title={<span className="flex justify-between items-center gap-2 flex-wrap">
      <span>Product Popularity — customer intent</span>
      <span className="mono-caption text-[var(--av-text-dim)]">
        {windowLabel} · {totals.paid_orders || 0} paid of {totals.attempted_orders || 0} attempted
        {totals.conversion_rate != null && <> · <span className="gold-text">{pct(totals.conversion_rate)} conversion</span></>}
      </span>
    </span>}>
      <div className="flex gap-2 mb-4" data-testid="popularity-window-toggle">
        {["7d","30d","all"].map(w => (
          <button key={w}
            data-testid={`popularity-window-${w}`}
            onClick={()=>setWindow(w)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${window===w ? "bg-[var(--av-gold)] text-black border-[var(--av-gold)]" : "border-[var(--av-border)] text-[var(--av-text-dim)] hover:text-[var(--av-gold)]"}`}>
            {w === "all" ? "All time" : w === "30d" ? "30 days" : "7 days"}
          </button>
        ))}
      </div>
      {groupList.length === 0 ? (
        <div className="text-sm text-[var(--av-text-dim)]">No orders yet in this window.</div>
      ) : (
        <div className="space-y-4" data-testid="popularity-groups">
          {groupList.map(g => {
            const gConv = Number(g.conversion_rate)||0;
            const maxPaid = Math.max(1, ...g.variants.map(v => v.paid_orders));
            return (
              <div key={g.apex_amount}
                   data-testid={`popularity-pack-${g.apex_amount}`}
                   className="rounded-xl border border-[var(--av-border)] bg-[rgba(212,175,55,0.03)] p-4">
                {/* Pack-level header — headline stats across every quantity */}
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <div>
                    <div className="font-serif text-lg gold-text">{g.apex_amount.toLocaleString()} Apex Pack</div>
                    <div className="text-[10px] text-[var(--av-text-dim)]">
                      {g.variants.length} quantity option{g.variants.length===1?"":"s"} ordered · {g.packs_sold} packs sold total
                    </div>
                  </div>
                  <div className="flex items-baseline gap-3 text-xs">
                    <span className="text-[var(--av-text-dim)]">
                      {g.paid_orders} paid <span className="text-[9px]">of</span> {g.attempted_orders} attempted
                    </span>
                    <span className={`font-bold ${gConv >= 0.7 ? "text-emerald-400" : gConv >= 0.4 ? "gold-text" : "text-red-400"}`}
                          data-testid={`popularity-pack-conv-${g.apex_amount}`}>
                      {pct(gConv)}
                    </span>
                    <span className="gold-text font-semibold">{fmt(g.revenue)}</span>
                  </div>
                </div>
                {/* Per-quantity breakdown */}
                <div className="overflow-x-auto">
                  <table className="vault-table" data-testid={`popularity-variants-${g.apex_amount}`}>
                    <thead>
                      <tr>
                        <th>Quantity</th>
                        <th className="text-right">Attempted</th>
                        <th className="text-right">Paid</th>
                        <th className="text-right">Abandoned</th>
                        <th className="text-right">Conversion</th>
                        <th className="text-right">Revenue</th>
                        <th className="text-right">Avg order</th>
                        <th className="text-right">Profit</th>
                        <th>Popularity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.variants.map(v => {
                        const w = Math.min(100, Math.round((v.paid_orders / maxPaid) * 100));
                        const vConv = Number(v.conversion_rate)||0;
                        const rowKey = `${v.apex_amount}-${v.pack_quantity || 1}`;
                        const qty = v.pack_quantity || 1;
                        return (
                          <tr key={rowKey} data-testid={`popularity-row-${rowKey}`}>
                            <td>
                              <div className="font-semibold">{qty} × pack</div>
                              <div className="text-[10px] text-[var(--av-text-dim)] font-mono">
                                = {(qty * v.apex_amount).toLocaleString()} apex
                              </div>
                            </td>
                            <td className="text-right">{v.attempted_orders}</td>
                            <td className="text-right font-semibold" data-testid={`popularity-paid-${rowKey}`}>{v.paid_orders}</td>
                            <td className="text-right text-[var(--av-text-dim)]">{v.abandoned_orders}</td>
                            <td className={`text-right font-semibold ${vConv >= 0.7 ? "text-emerald-400" : vConv >= 0.4 ? "text-[var(--av-gold)]" : "text-red-400"}`}>
                              {pct(vConv)}
                            </td>
                            <td className="text-right">{fmt(v.revenue)}</td>
                            <td className="text-right text-[var(--av-text-dim)]">{fmt(v.avg_order_value)}</td>
                            <td className={`text-right font-semibold ${(v.profit||0) >= 0 ? "gold-text" : "text-red-400"}`}>{fmt(v.profit)}</td>
                            <td>
                              <div className="h-2 w-24 rounded-full bg-[var(--av-border)]/40 overflow-hidden">
                                <div className="h-full bg-[var(--av-gold)]" style={{width: `${w}%`}} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {/* All-pack totals footer */}
          <div className="flex flex-wrap justify-between items-baseline gap-3 pt-3 border-t border-[var(--av-border)]">
            <span className="mono-caption text-[var(--av-text-dim)]">ALL PACKS — TOTALS</span>
            <div className="flex items-baseline gap-4 text-xs">
              <span>{totals.paid_orders || 0} paid of {totals.attempted_orders || 0} attempted</span>
              <span className={`font-bold ${(totals.conversion_rate||0) >= 0.5 ? "gold-text" : "text-red-400"}`}>
                {pct(totals.conversion_rate)}
              </span>
              <span className="gold-text font-semibold">{fmt(totals.revenue)}</span>
              <span className={`font-semibold ${(totals.profit||0) >= 0 ? "gold-text" : "text-red-400"}`}
                    data-testid="popularity-totals-profit">
                {fmt(totals.profit)}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="text-[10px] text-[var(--av-text-dim)] mt-3">
        Auto-refreshes every 60s. Attempted = every checkout including abandoned. Conversion = paid ÷ attempted.
      </div>
    </Card>
  );
}

/**
 * Day-by-day P&L since the first paid order.
 * Uses the SAME fee/profit formulas as the tiles above — card fee is
 * cash × 1.5% + £0.26 unless a real Stripe fee is on the order row.
 */
function DailyProfitBreakdown({ daily }) {
  const [expand, setExpand] = useState(false);
  if (!daily) return (
    <Card title="Daily Profit — since launch">
      <div className="text-sm text-[var(--av-text-dim)]">Loading…</div>
    </Card>
  );
  const rows = (daily.days || []);
  const totals = daily.totals || {};
  const fmt = n => `£${(Number(n)||0).toFixed(2)}`;
  // Newest-first for the table; keep chronological order in `daily.days`
  // so a future chart can consume it directly.
  const sorted = [...rows].reverse();
  const visible = expand ? sorted : sorted.slice(0, 14);
  const maxProfit = Math.max(1, ...rows.map(r => Math.abs(Number(r.profit)||0)));
  return (
    <Card title={<span className="flex justify-between items-center gap-2 flex-wrap">
      <span>Daily Profit — since launch</span>
      <span className="mono-caption text-[var(--av-text-dim)]">
        {totals.days || 0} days · Σ profit <span className="gold-text">{fmt(totals.profit)}</span>
      </span>
    </span>}>
      {rows.length === 0 ? (
        <div className="text-sm text-[var(--av-text-dim)]">No paid orders yet.</div>
      ) : <>
        <div className="text-[10px] text-[var(--av-text-dim)] mb-2" data-testid="daily-profit-formula">
          {daily.fee_formula || "Card: cash × 1.5% + £0.26  |  Pay by Bank: cash × 0.5%"}
        </div>
        <div className="overflow-x-auto">
          <table className="vault-table" data-testid="daily-profit-table">
            <thead>
              <tr>
                <th>Date</th><th className="text-right">Orders</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Supplier</th>
                <th className="text-right">Fees</th>
                <th className="text-right">Profit</th>
                <th>Trend</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const p = Number(r.profit) || 0;
                const w = Math.min(100, Math.round((Math.abs(p)/maxProfit)*100));
                return (
                  <tr key={r.date} data-testid={`daily-profit-row-${r.date}`}>
                    <td className="font-mono text-xs">{r.date}</td>
                    <td className="text-right">{r.orders}</td>
                    <td className="text-right">{fmt(r.revenue)}</td>
                    <td className="text-right text-[var(--av-text-dim)]">{fmt(r.supplier_cost)}</td>
                    <td className="text-right text-[var(--av-text-dim)]">{fmt(r.payment_fees)}</td>
                    <td className={`text-right font-semibold ${p >= 0 ? "text-emerald-400" : "text-red-400"}`}
                        data-testid={`daily-profit-value-${r.date}`}>
                      {fmt(p)}
                    </td>
                    <td>
                      <div className="h-2 w-24 rounded-full bg-[var(--av-border)]/40 overflow-hidden">
                        <div className={`h-full ${p >= 0 ? "bg-[var(--av-gold)]" : "bg-red-500"}`}
                             style={{width: `${w}%`}} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-[var(--av-border)]">
                <td className="mono-caption">TOTALS</td>
                <td className="text-right font-semibold">{totals.orders || 0}</td>
                <td className="text-right font-semibold">{fmt(totals.revenue)}</td>
                <td className="text-right text-[var(--av-text-dim)]">{fmt(totals.supplier_cost)}</td>
                <td className="text-right text-[var(--av-text-dim)]">{fmt(totals.payment_fees)}</td>
                <td className={`text-right font-bold ${(totals.profit||0) >= 0 ? "gold-text" : "text-red-400"}`}
                    data-testid="daily-profit-totals-profit">
                  {fmt(totals.profit)}
                </td>
                <td/>
              </tr>
            </tfoot>
          </table>
        </div>
        {sorted.length > 14 && (
          <button className="btn-outline-gold text-xs mt-3"
                  data-testid="daily-profit-expand"
                  onClick={()=>setExpand(v=>!v)}>
            {expand ? "Show last 14 days" : `Show all ${sorted.length} days`}
          </button>
        )}
      </>}
    </Card>
  );
}

function Orders() {
  const [rows, setRows] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [showRefunds, setShowRefunds] = useState(false);
  const [refundOrder, setRefundOrder] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [statusOrder, setStatusOrder] = useState(null);
  const [showDispatch, setShowDispatch] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const load = () => {
    api.get("/admin/orders").then(r=>setRows(r.data));
    api.get("/admin/refunds").then(r=>setRefunds(r.data.rows || [])).catch(()=>setRefunds([]));
  };
  useEffect(()=>{ load(); },[]);
  // Filter + auto-scroll: match by order ID, customer email/name, PI, or IGN.
  const s = orderSearch.trim().toLowerCase();
  const filtered = !s ? rows : rows.filter(o =>
    (o.id || "").toLowerCase().includes(s)
    || (o.customer_email || "").toLowerCase().includes(s)
    || (o.customer_name || "").toLowerCase().includes(s)
    || (o.customer_in_game_name || "").toLowerCase().includes(s)
    || (o.stripe_payment_intent_id || "").toLowerCase().includes(s)
  );
  const jumpTo = () => {
    if (!s) return;
    const first = filtered[0];
    if (!first) return toast.error("No matching order");
    const row = document.querySelector(`[data-testid="admin-order-row-${first.id}"]`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(first.id);
      setTimeout(() => setHighlightId(null), 2500);
    }
  };
  const dl = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/orders.csv?t=${localStorage.getItem("av_token")}`,"_blank");
  const dlArchive = async () => {
    try {
      const { data } = await api.get("/admin/reports/full-archive.json");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `arthurs-vault-archive-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Archive downloaded (${data.counts.orders} orders, ${data.counts.customers} customers)`);
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed to fetch archive"); }
  };
  const backfillTierSpend = async () => {
    if (!window.confirm("Retroactively credit tier spend on every paid order that missed it? Safe to re-run.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/backfill-qualifying-spend");
      toast.success(`Credited ${data.credited} of ${data.total_paid_scanned} paid orders (£${data.credited_gbp.toFixed(2)})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
    setBackfilling(false);
  };
  const forceReconcile = async (o) => {
    if (!window.confirm(`Force-reconcile ${o.id} with OLD STORE?\n\nThis polls the vendor for the current status and, if they say completed, marks the order COMPLETED and fires the Telegram alert. Safe to run more than once.`)) return;
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/force-reconcile`);
      if (data.vendor_status === "completed" && data.completion_fired) {
        toast.success(`✓ ${o.id} marked COMPLETED · Telegram sent`);
      } else if (data.vendor_status === "completed") {
        toast.success(`${o.id} was already marked completed (no double-fire)`);
      } else {
        toast(`Vendor status: ${data.vendor_status || "unknown"}`, { icon: "ℹ️" });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force-reconcile failed");
    }
  };
  const canRefund = o => o.payment_status === "paid" && !["REFUNDED"].includes(o.status);
  return (<>
    <Card title={<span className="flex justify-between items-center gap-2 flex-wrap">All Orders
      <span className="flex gap-2 flex-wrap">
        <button data-testid="show-refunds-btn" onClick={()=>setShowRefunds(v=>!v)}
                className={`text-xs px-3 py-1 rounded ${showRefunds ? "btn-gold" : "btn-outline-gold"}`}>
          Refunds ({refunds.length})
        </button>
        <button data-testid="manual-dispatch-btn" onClick={()=>setShowDispatch(true)} className="btn-gold text-xs">📦 Manual Dispatch</button>
        <button data-testid="tier-spend-backfill-btn" onClick={backfillTierSpend} disabled={backfilling} className="btn-outline-gold text-xs disabled:opacity-60">{backfilling ? "Crediting…" : "Backfill Tier Spend"}</button>
        <button data-testid="archive-export-btn" onClick={dlArchive} className="btn-outline-gold text-xs">Download Full Archive (JSON)</button>
        <button data-testid="csv-export-btn" onClick={dl} className="btn-outline-gold text-xs">Export CSV</button>
      </span>
    </span>}>
      {showRefunds && (
        <div className="vault-card p-3 mb-4 border-[var(--av-gold)]/30" data-testid="refunds-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="mono-caption">All Refunds ({refunds.length}) · newest first</div>
            <button onClick={() => setShowRefunds(false)}
                    data-testid="refunds-panel-close"
                    className="text-[var(--av-text-dim)] hover:text-[var(--av-gold)] text-xs">Hide</button>
          </div>
          <div className="overflow-x-auto">
            <table className="vault-table text-xs">
              <thead><tr>
                <th>Date</th><th>Order</th><th>Customer</th><th>Product</th>
                <th>Amount</th><th>Reason</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {refunds.map(r => (
                  <tr key={r.id} data-testid={`refund-row-${r.id}`}>
                    <td>{(r.created_at || "").slice(0,16).replace("T"," ") || "N/A"}</td>
                    <td className="font-mono">{r.order_id || "N/A"}</td>
                    <td>
                      <div>{r.customer_name || "—"}</div>
                      <div className="text-[10px] text-[var(--av-text-dim)] font-mono">{r.customer_email || "N/A"}</div>
                    </td>
                    <td className="truncate max-w-[180px]">{r.product_name || "N/A"}</td>
                    <td className="text-red-300 font-semibold">£{Number(r.amount || (r.stripe_amount_pence || 0) / 100).toFixed(2)}</td>
                    <td className="text-[var(--av-text-dim)] truncate max-w-[200px]">{r.reason || "—"}</td>
                    <td><span className="pill pill-gold text-[10px]">{r.stripe_status || r.status || "processed"}</span></td>
                    <td>
                      {r.order_id && (
                        <button
                          data-testid={`refund-goto-${r.id}`}
                          onClick={() => {
                            setOrderSearch(r.order_id);
                            setTimeout(() => {
                              const row = document.querySelector(`[data-testid="admin-order-row-${r.order_id}"]`);
                              if (row) {
                                row.scrollIntoView({ behavior: "smooth", block: "center" });
                                setHighlightId(r.order_id);
                                setTimeout(() => setHighlightId(null), 2500);
                              }
                            }, 100);
                          }}
                          className="btn-outline-gold text-[10px] px-2 py-0.5">Open Order</button>
                      )}
                    </td>
                  </tr>
                ))}
                {refunds.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-4 text-[var(--av-text-dim)]">No refunds yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          data-testid="order-search"
          className="input-vault w-full sm:w-96 text-sm"
          placeholder="Search by order number, PI, customer email, name or IGN"
          value={orderSearch}
          onChange={e => setOrderSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && jumpTo()}
        />
        <button data-testid="order-search-jump" onClick={jumpTo}
                disabled={!s}
                className="btn-outline-gold text-xs disabled:opacity-40">Jump to first match</button>
        <span className="text-xs text-[var(--av-text-muted)]">{filtered.length} of {rows.length}</span>
        {orderSearch && (
          <button data-testid="order-search-clear" onClick={() => setOrderSearch("")}
                  className="text-xs text-[var(--av-text-dim)] hover:text-[var(--av-gold)]">Clear</button>
        )}
      </div>
      <table className="vault-table">
        <thead><tr><th>Order</th><th>Customer</th><th>Created</th><th>Apex</th><th>Cash</th><th>Rewards Used</th><th>Profit</th><th>Method</th><th>Referral</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>{filtered.map(o=>(
          <tr key={o.id} data-testid={`admin-order-row-${o.id}`}
              className={`${o.manual_dispatch ? "bg-[rgba(148,0,211,0.05)]" : ""} ${highlightId === o.id ? "ring-2 ring-[var(--av-gold)] transition-all" : ""}`}>
            <td className="font-mono text-xs">
              {o.id}
              {o.manual_dispatch && <div className="text-[10px] purple-text">MANUAL</div>}
            </td>
            <td className="text-xs">
              <div className="font-semibold">{o.customer_name || "—"}</div>
              {o.customer_in_game_name && <div className="text-[10px] text-[var(--av-gold)]/80">IGN: {o.customer_in_game_name}</div>}
              <div className="text-[10px] text-[var(--av-text-dim)] font-mono">{o.customer_email}</div>
            </td>
            <td className="text-xs">{o.created_at?.slice(0,16).replace("T"," ")}</td>
            <td>{o.package_snapshot?.apex_amount?.toLocaleString()}</td>
            <td>£{o.cash_to_pay?.toFixed(2)}</td>
            <td data-testid={`rewards-used-${o.id}`} className={Number(o.reward_redemption) > 0 ? "purple-text font-semibold" : "text-[var(--av-text-dim)]"}>
              {Number(o.reward_redemption) > 0 ? `£${Number(o.reward_redemption).toFixed(2)}` : "—"}
            </td>
            <td data-testid={`profit-${o.id}`} className={o.profit_gbp != null && o.profit_gbp >= 0 ? "text-emerald-400" : "text-red-400"}>
              {o.profit_gbp != null ? `£${o.profit_gbp.toFixed(2)}` : "—"}
            </td>
            <td>{o.payment_method}</td>
            <td data-testid={`referral-${o.id}`} className="text-xs">
              {o.referral_code ? (
                <>
                  <div className="font-mono text-[var(--av-gold)]">{o.referral_code}</div>
                  <div className="text-[10px] text-[var(--av-text-dim)]">{o.referrer_name || o.referrer_email || "—"}</div>
                  <div className="text-[10px] purple-text">£{Number(o.referrer_reward_gbp || 0).toFixed(2)} → referrer</div>
                </>
              ) : <span className="text-[var(--av-text-dim)]">—</span>}
            </td>
            <td><span className="pill pill-gold">{o.status}</span></td>
            <td className="whitespace-nowrap">
              <div className="flex gap-1 flex-wrap">
                <button data-testid={`status-btn-${o.id}`} onClick={()=>setStatusOrder(o)} className="btn-outline-gold text-xs px-2" title="Manually change status">Status</button>
                {o.supplier_order_id && !["COMPLETED","REFUNDED","CANCELLED"].includes(o.status) && (
                  <button data-testid={`force-reconcile-${o.id}`} onClick={()=>forceReconcile(o)} className="btn-outline-gold text-xs px-2" title="Poll supplier + mark completed if they say so">Reconcile</button>
                )}
                {canRefund(o) && <button data-testid={`refund-btn-${o.id}`} onClick={()=>setRefundOrder(o)} className="btn-outline-gold text-xs px-2">Refund</button>}
              </div>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </Card>
    {refundOrder && <RefundDialog order={refundOrder} onClose={()=>{ setRefundOrder(null); load(); }}/>}
    {statusOrder && <StatusOverrideDialog order={statusOrder} onClose={()=>{ setStatusOrder(null); load(); }}/>}
    {showDispatch && <ManualDispatchDialog onClose={()=>{ setShowDispatch(false); load(); }}/>}
  </>);
}

/**
 * OWNER-ONLY Manual Dispatch — send an order directly to OLD STORE
 * without a customer, a Stripe charge, or any reward/referral crediting.
 * Debits the supplier wallet as normal. Used for offline compensation
 * cases, owner top-ups, or production supplier-flow smoke tests.
 * Backend endpoint: POST /api/admin/orders/manual-dispatch (owner_dep).
 */
function ManualDispatchDialog({ onClose }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [serverId, setServerId] = useState("");
  const [governorUid, setGovernorUid] = useState("");
  const [ign, setIgn] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/apex-products").then(r => setProducts(r.data || []));
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!productId || !serverId || !governorUid) {
      return toast.error("Product, Server ID and Governor UID are all required.");
    }
    const q = parseInt(quantity, 10);
    if (!q || q < 1) return toast.error("Quantity must be at least 1.");
    if (!window.confirm(`Dispatch ${q}× ${products.find(p=>p.id===productId)?.name || "Apex pack"} to ${serverId} / ${governorUid}? This will DEBIT your supplier wallet immediately. No customer is charged.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders/manual-dispatch", {
        apex_product_id: productId, quantity: q,
        server_id: serverId.trim().toUpperCase(),
        governor_uid: governorUid.trim(),
        in_game_name: ign.trim() || null,
        note: note.trim() || null,
      });
      toast.success(`✓ Dispatched ${data.apex_dispatched.toLocaleString()} Apex → AV order ${data.order_id}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Manual dispatch failed");
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="manual-dispatch-dialog" className="vault-card p-6 max-w-md w-full space-y-4" onClick={e=>e.stopPropagation()}>
        <div>
          <div className="mono-caption text-red-400">⚠ Owner Only — Bypasses Customer Payment</div>
          <h3 className="font-serif text-2xl gold-text mt-1">Manual Dispatch</h3>
          <p className="text-[11px] text-[var(--av-text-dim)] leading-snug mt-2">
            Sends an order straight to OLD STORE and debits your supplier wallet immediately. No Stripe charge, no customer email, no rewards, no referral credit. The order will appear in the Orders table tagged <span className="font-mono text-[var(--av-gold)]">manual_dispatch</span>.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-xs block">Apex Pack
            <select required data-testid="dispatch-product" value={productId}
              onChange={e=>setProductId(e.target.value)} className="input-vault mt-1">
              <option value="">— select —</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>{p.name} · {p.apex_amount.toLocaleString()} Apex</option>
              ))}
            </select>
          </label>
          <label className="text-xs block">Quantity (packs)
            <input required type="number" min="1" max="100" step="1"
              data-testid="dispatch-quantity" value={quantity}
              onChange={e=>setQuantity(e.target.value)} className="input-vault mt-1"/>
          </label>
          <label className="text-xs block">Server ID
            <input required data-testid="dispatch-server-id" value={serverId}
              onChange={e=>setServerId(e.target.value)}
              onBlur={e=>{
                const raw = String(e.target.value ?? "").replace(/[^0-9]/g, "");
                if (!raw) return setServerId("");
                const n = parseInt(raw, 10);
                setServerId(n < 10 ? `E0${n}` : `E${n}`);
              }}
              placeholder="e.g. E05" className="input-vault mt-1 font-mono uppercase"/>
            <span className="text-[10px] text-[var(--av-text-dim)] block mt-1">Server 5 → E05, server 175 → E175. Auto-formats on tab-out.</span>
          </label>
          <label className="text-xs block">Governor UID
            <input required data-testid="dispatch-governor-uid" value={governorUid}
              onChange={e=>setGovernorUid(e.target.value)}
              placeholder="numbers only, no spaces" className="input-vault mt-1 font-mono"/>
          </label>
          <label className="text-xs block">In-Game Name (optional)
            <input data-testid="dispatch-ign" value={ign}
              onChange={e=>setIgn(e.target.value)} className="input-vault mt-1"/>
          </label>
          <label className="text-xs block">Note (audit-log, optional)
            <textarea rows="2" data-testid="dispatch-note" value={note}
              onChange={e=>setNote(e.target.value)} className="input-vault mt-1"
              placeholder="Why are you dispatching manually? e.g. support compensation for AV-XXXXXX"/>
          </label>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold text-xs">Cancel</button>
            <button type="submit" data-testid="dispatch-submit" disabled={busy} className="btn-gold text-xs disabled:opacity-60">
              {busy ? "Dispatching…" : "Send to Supplier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Manual status override dialog. The admin is authoritative here — every
 * canonical status is selectable regardless of the order's current state,
 * so a stuck order (e.g. a missed completion webhook) can be unstuck in
 * one click. Choosing `COMPLETED` fires the same Telegram card and
 * customer notification as the webhook would have.
 */
function StatusOverrideDialog({ order, onClose }) {
  const [status, setStatus] = useState(order.status || "COMPLETED");
  const [note, setNote] = useState("");
  const [supplierRef, setSupplierRef] = useState(order.supplier_order_id || "");
  const [busy, setBusy] = useState(false);
  const OPTIONS = [
    "WAITING FOR FULFILMENT",
    "SENT TO SUPPLIER",
    "SUPPLIER PROCESSING",
    "PROCESSING",
    "SUBMITTING TO SUPPLIER",
    "AWAITING CUSTOMER CONFIRMATION",
    "COMPLETED",
    "DELIVERED",
    "FULFILMENT FAILED",
    "CUSTOMER ISSUE",
    "INSUFFICIENT SUPPLIER BALANCE",
    "CANCELLED",
    "REFUNDED",
    "PARTIALLY REFUNDED",
  ];
  const save = async () => {
    setBusy(true);
    try {
      const body = { status, note: note || null };
      if (supplierRef) body.supplier_reference = supplierRef;
      const { data } = await api.post(`/admin/orders/${order.id}/status`, body);
      if (status === "COMPLETED") {
        toast.success(`✓ ${order.id} marked COMPLETED · Telegram fired`);
      } else {
        toast.success(`✓ ${order.id} → ${data.status}`);
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="status-override-dialog" className="vault-card p-6 max-w-md w-full space-y-4" onClick={e=>e.stopPropagation()}>
        <div>
          <div className="mono-caption">Manual Status Override</div>
          <div className="text-xs text-[var(--av-text-dim)] font-mono mt-1">{order.id}</div>
          <div className="text-xs text-[var(--av-text-muted)] mt-1">Currently: <span className="pill pill-gold">{order.status}</span></div>
        </div>
        <div>
          <label className="text-xs mono-caption block mb-1">New Status</label>
          <select data-testid="status-select" value={status} onChange={e=>setStatus(e.target.value)} className="input-vault text-sm">
            {OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {status === "COMPLETED" && (
            <div className="text-[11px] text-emerald-400 mt-2">
              Will fire the "✅ ORDER FULFILLED" Telegram card and flip the customer view to FULFILLED. Idempotent — safe to click on an already-completed order.
            </div>
          )}
        </div>
        <div>
          <label className="text-xs mono-caption block mb-1">Supplier Reference (optional)</label>
          <input data-testid="status-supplier-ref" value={supplierRef} onChange={e=>setSupplierRef(e.target.value)} placeholder="ODS-…" className="input-vault text-sm font-mono"/>
        </div>
        <div>
          <label className="text-xs mono-caption block mb-1">Note (audit-log)</label>
          <textarea data-testid="status-note" rows="2" value={note} onChange={e=>setNote(e.target.value)} placeholder="Why is this change being made?" className="input-vault text-sm"/>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-outline-gold text-xs">Cancel</button>
          <button data-testid="status-save-btn" onClick={save} disabled={busy} className="btn-gold text-xs disabled:opacity-60">
            {busy ? "Saving…" : "Save Change"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RefundDialog({ order, onClose }) {
  const [amount, setAmount] = useState(order.cash_to_pay.toFixed(2));
  const [reason, setReason] = useState("");
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pack-based refund state — hydrated from the /refunds endpoint which
  // also returns the order's pack_quantity and remaining refundable packs.
  const [mode, setMode] = useState("amount"); // "amount" | "packs"
  const [packs, setPacks] = useState(1);
  useEffect(()=>{
    api.get(`/admin/orders/${order.id}/refunds`).then(r=>{
      setExisting(r.data);
      const remainingPacks = Math.max(0, (r.data.remaining_packs ?? r.data.pack_quantity) || 0);
      // Auto-select By packs when the order has 2+ packs still refundable.
      if ((r.data.pack_quantity || 1) > 1 && remainingPacks > 0) {
        setMode("packs");
        setPacks(Math.min(remainingPacks, 1));
      }
    });
  },[order]);
  const pack_quantity = existing?.pack_quantity || 1;
  const already_packs = existing?.total_refunded_packs || 0;
  const remaining_packs = Math.max(0, pack_quantity - already_packs);
  const remaining = existing ? Math.max(0, order.cash_to_pay - existing.total_refunded) : order.cash_to_pay;
  const unit_paid = pack_quantity > 0 ? order.cash_to_pay / pack_quantity : 0;
  // If "By packs", derived amount is pro-rata paid-cash, snapped to remaining
  // when refunding every remaining pack to avoid 1-penny drift.
  const packs_num = Math.max(1, Math.min(remaining_packs || 1, parseInt(packs || 1, 10) || 1));
  const derivedAmount = mode === "packs"
    ? (packs_num >= remaining_packs ? remaining : Math.min(remaining, Math.round(order.cash_to_pay * (packs_num / pack_quantity) * 100) / 100))
    : parseFloat(amount);
  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = mode === "packs"
        ? { packs: packs_num, reason }
        : { amount: parseFloat(amount), reason };
      const { data } = await api.post(`/admin/orders/${order.id}/refund`, body);
      const packsMsg = data.packs ? ` (${data.packs} of ${pack_quantity} packs)` : "";
      toast.success(`Refund of £${data.amount.toFixed(2)}${packsMsg} processed — order ${data.status}`);
      onClose();
    } catch(err){ toast.error(err?.response?.data?.detail || "Refund failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="vault-card p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
        <div className="mono-caption mb-2">Refund Order</div>
        <h3 className="font-serif text-2xl gold-text mb-3">{order.id}</h3>
        <div className="text-sm text-[var(--av-text-dim)] mb-4">
          <div>Paid: <span className="text-[var(--av-text)]">£{order.cash_to_pay.toFixed(2)}</span>
            {pack_quantity > 1 && <span className="text-[var(--av-text-dim)]"> — {pack_quantity} packs @ £{unit_paid.toFixed(2)} ea</span>}
          </div>
          {existing && <>
            <div>Already refunded: £{existing.total_refunded.toFixed(2)}{already_packs > 0 && ` (${already_packs} packs)`}</div>
            <div>Refundable: <span className="gold-text font-bold">£{remaining.toFixed(2)}</span>
              {pack_quantity > 1 && <span> · {remaining_packs} of {pack_quantity} packs</span>}
            </div>
          </>}
        </div>
        {pack_quantity > 1 && remaining_packs > 0 && (
          <div className="flex gap-2 mb-3" data-testid="refund-mode-toggle">
            <button type="button"
              data-testid="refund-mode-packs"
              onClick={()=>setMode("packs")}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${mode==="packs" ? "bg-[var(--av-gold)] text-black border-[var(--av-gold)]" : "border-[var(--av-border)] text-[var(--av-text-dim)] hover:text-[var(--av-gold)]"}`}>
              By packs
            </button>
            <button type="button"
              data-testid="refund-mode-amount"
              onClick={()=>setMode("amount")}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${mode==="amount" ? "bg-[var(--av-gold)] text-black border-[var(--av-gold)]" : "border-[var(--av-border)] text-[var(--av-text-dim)] hover:text-[var(--av-gold)]"}`}>
              By amount
            </button>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          {mode === "packs" ? (
            <label className="text-xs block">Packs to refund (of {pack_quantity})
              <input required type="number" step="1" min="1" max={remaining_packs}
                data-testid="refund-packs"
                className="input-vault mt-1" value={packs} onChange={e=>setPacks(e.target.value)}/>
              <span className="text-[10px] text-[var(--av-text-dim)] mt-1 inline-block" data-testid="refund-derived-amount">
                → refund £{derivedAmount.toFixed(2)} (pro-rated from cash paid)
              </span>
            </label>
          ) : (
            <label className="text-xs block">Amount (£)
              <input required type="number" step="0.01" max={remaining} min="0.01"
                data-testid="refund-amount"
                className="input-vault mt-1" value={amount} onChange={e=>setAmount(e.target.value)}/>
            </label>
          )}
          <label className="text-xs block">Reason
            <textarea required rows="3" data-testid="refund-reason" className="input-vault mt-1"
              value={reason} onChange={e=>setReason(e.target.value)}
              placeholder="Why is this refund being issued?"/>
          </label>
          <p className="text-xs text-[var(--av-text-dim)]">This will refund the customer via Stripe and automatically reverse the linked Apex Rewards (redemption + earned) pro-rata. Full refunds also reverse any referrer reward.</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button disabled={busy} data-testid="refund-submit" className="btn-gold text-sm flex-1">{busy?"Processing…":"Issue Refund"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Reconciliation() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { setBusy(true); try { const { data } = await api.get("/admin/reconciliation"); setD(data); } catch { toast.error("Failed to load"); } setBusy(false); };
  useEffect(()=>{ load(); },[]);
  const sev = s => s === "high" ? "text-red-400 border-red-500/40 bg-red-500/5" : "text-yellow-400 border-yellow-500/30 bg-yellow-500/5";
  const label = t => ({
    stripe_no_local_record: "Stripe payment with no Arthur's Vault order",
    stripe_paid_local_pending: "Stripe paid but Vault record still pending",
    paid_not_fulfilled_24h: "Paid > 24h, not fulfilled",
    duplicate_orders_same_session: "Duplicate orders on same Stripe session",
  }[t] || t);
  if (!d) return <div className="text-[var(--av-text-dim)]">Loading…</div>;
  return (
    <div className="space-y-4">
      <Card title={<span className="flex justify-between items-center">Reconciliation ({d.window})<button data-testid="reconcile-refresh" onClick={load} className="btn-outline-gold text-xs">{busy?"…":"Re-run"}</button></span>}>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div><div className="mono-caption">Stripe sessions</div><div className="font-serif text-2xl gold-text">{d.stripe_sessions_scanned}</div></div>
          <div><div className="mono-caption">Flags</div><div className="font-serif text-2xl purple-text">{d.flags.length}</div></div>
          <div><div className="mono-caption">Checked</div><div className="text-xs mt-2">{d.checked_at?.slice(0,16).replace("T"," ")}</div></div>
        </div>
        {d.flags.length === 0 && <div className="text-sm text-[var(--av-gold)]">✓ No discrepancies detected. Stripe and the Vault database are aligned.</div>}
        <div className="space-y-2">
          {d.flags.map((f, i) => {
            const flagKey = `${f.type}:${f.session_id || f.order_id || (f.order_ids && f.order_ids.join(',')) || i}`;
            return (
              <div key={flagKey} data-testid={`flag-row-${i}`} className={`p-3 rounded border ${sev(f.severity)}`}>
              <div className="flex justify-between text-sm"><span className="font-semibold">{label(f.type)}</span><span className="pill pill-gold">{f.severity}</span></div>
              <div className="text-xs mt-1 text-[var(--av-text-dim)] font-mono">
                {f.session_id && <span>Session: {f.session_id} · </span>}
                {f.order_id && <span>Order: {f.order_id} · </span>}
                {f.order_ids && <span>Orders: {f.order_ids.join(", ")} · </span>}
                {f.amount != null && <span>£{f.amount.toFixed(2)} · </span>}
                {f.at && <span>{f.at.slice(0,16).replace("T"," ")}</span>}
                {f.paid_at && <span>Paid: {f.paid_at.slice(0,16).replace("T"," ")}</span>}
              </div>
            </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function Fulfilment() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = () => api.get("/admin/fulfilment-queue").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);
  const view = async o => { setSelected(o); const { data } = await api.get(`/admin/orders/${o.id}/supplier-message`); setMsg(data); };
  const setStatus = async (id, status) => { await api.post(`/admin/orders/${id}/status`, { status }); toast.success(`Status: ${status}`); load(); setSelected(null); };
  return (<div className="grid lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2 min-w-0"><Card title="Fulfilment Queue">
      <table className="vault-table"><thead><tr><th>Order</th><th>Age</th><th>Cash</th><th>Apex</th><th>Server</th><th>Status</th><th></th></tr></thead>
      <tbody>{rows.map(o=>(
        <tr key={o.id}>
          <td className="font-mono">{o.id}</td>
          <td className="text-xs">{o.paid_at?.slice(0,16).replace("T"," ")}</td>
          <td>£{Number(o.cash_to_pay||0).toFixed(2)}</td>
          <td>{o.package_snapshot?.apex_amount?.toLocaleString()}</td>
          <td>{o.game_profile_snapshot?.server_id}</td>
          <td><span className="pill pill-gold">{o.status}</span></td>
          <td className="whitespace-nowrap">
            <button data-testid={`fulfil-view-${o.id}`} onClick={()=>view(o)} className="btn-outline-gold text-xs">Open</button>
          </td>
        </tr>
      ))}
      {rows.length===0 && <tr><td colSpan="8" className="text-center py-6 text-[var(--av-text-dim)]">Queue empty.</td></tr>}</tbody></table>
    </Card></div>
    <div>{selected && msg && (<Card title={`${selected.id}`}>
      <div className="text-xs mono-caption mb-2">Supplier Message</div>
      <textarea readOnly rows="10" className="input-vault text-xs font-mono" value={msg.message}/>
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={()=>{ navigator.clipboard.writeText(msg.message); toast.success("Copied — paste into your supplier chat"); }} className="btn-outline-gold text-xs">Copy Message</button>
        <button data-testid={`mark-sent-${selected.id}`} onClick={()=>setStatus(selected.id,"SENT TO SUPPLIER")} className="btn-outline-gold text-xs">Mark Sent</button>
        <button onClick={()=>setStatus(selected.id,"SUPPLIER PROCESSING")} className="btn-outline-gold text-xs">Processing</button>
        <button data-testid={`mark-fulfilled-${selected.id}`} onClick={()=>setStatus(selected.id,"FULFILLED")} className="btn-gold text-xs">Mark Fulfilled</button>
      </div>
    </Card>)}</div>
  </div>);
}

// Shared save-button label helper — replaces four nested ternaries across
// ApexEditor / EmpireEditor / OfferEditor / PromoEditor.
function saveBtnLabel(busy, isNew, createLabel = "Create") {
  if (busy) return "Saving…";
  if (isNew) return createLabel;
  return "Save Changes";
}

function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [gift, setGift] = useState(null); // customer being gifted
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewOrders, setViewOrders] = useState(null); // {customer, orders} for modal
  const TIER_NAMES = ["Bronze", "Silver", "Gold", "Platinum", "Diamond", "Apex Elite"];

  const refresh = () => api.get("/admin/customers").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);

  const filtered = rows.filter(u => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (u.id || "").toLowerCase().includes(s)
      || (u.email || "").toLowerCase().includes(s)
      || `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase().includes(s);
  });

  const openGift = (u) => { setGift(u); setAmount(""); setReason(""); };

  const submitGift = async () => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a positive amount");
    if (!reason.trim()) return toast.error("Please add a reason (customer sees this in their email)");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rewards/adjust", {
        user_id: gift.id, amount: amt, reason: reason.trim(), tx_type: "GIFT",
      });
      toast.success(`Gifted £${amt.toFixed(2)} — new balance £${data.new_balance.toFixed(2)}`);
      setGift(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to gift rewards");
    }
    setBusy(false);
  };

  return (
    <Card title="Customers">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          data-testid="customer-search"
          className="input-vault w-full sm:w-96 text-sm"
          placeholder="Search by Customer ID (USR-…), email or name"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <span className="text-xs text-[var(--av-text-muted)]">{filtered.length} of {rows.length}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="vault-table">
          <thead><tr>
            <th>Customer ID</th><th>Name</th><th>Email</th>
            <th>Orders</th><th>Revenue</th><th>Profit</th>
            <th>Tier Spend</th><th>Rewards</th><th>Tier</th><th>Role</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} data-testid={`customer-row-${u.id}`}>
                <td className="font-mono text-xs">{u.id}</td>
                <td>{u.first_name} {u.last_name}</td>
                <td className="text-xs">{u.email}</td>
                <td data-testid={`orders-${u.id}`}>{u.paid_orders_count ?? 0}</td>
                <td>£{Number(u.lifetime_revenue_gbp ?? 0).toFixed(2)}</td>
                <td data-testid={`profit-${u.id}`} className={(u.lifetime_profit_gbp ?? 0) >= 0 ? "text-emerald-400 font-semibold" : "text-red-400 font-semibold"}>
                  £{Number(u.lifetime_profit_gbp ?? 0).toFixed(2)}
                </td>
                <td>£{u.qualifying_spend?.toFixed(2)}</td>
                <td className="purple-text font-semibold">£{u.reward_available?.toFixed(2)}</td>
                <td>
                  <select
                    data-testid={`tier-select-${u.id}`}
                    value={u.tier_override || ""}
                    disabled={busy}
                    onChange={async (e) => {
                      const val = e.target.value;
                      try {
                        setBusy(true);
                        const { data } = await api.put(`/admin/customers/${u.id}/tier`, { tier_name: val || null });
                        toast.success(val
                          ? `Tier set to ${data.effective_tier} (override)`
                          : `Override cleared — now ${data.effective_tier} (auto)`);
                        refresh();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Failed to change tier");
                      } finally { setBusy(false); }
                    }}
                    className="input-vault text-xs py-1 pr-6 min-w-[120px]"
                    title={u.tier_override ? `Overridden — auto would be ${u.effective_tier}` : "Auto (spend-based)"}
                  >
                    <option value="">Auto — {u.effective_tier || "Bronze"}</option>
                    {TIER_NAMES.map(t => (
                      <option key={t} value={t}>{t}{u.tier_override === t ? " ✓" : ""}</option>
                    ))}
                  </select>
                </td>
                <td><span className="pill pill-gold">{u.role}</span></td>
                <td>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`view-orders-btn-${u.id}`}
                      onClick={async () => {
                        try {
                          setBusy(true);
                          const { data } = await api.get(`/admin/customers/${u.id}/orders`);
                          setViewOrders(data);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Failed to load orders");
                        } finally { setBusy(false); }
                      }}
                      disabled={busy}
                      className="btn-outline-gold text-xs px-3 py-1 flex items-center gap-1 disabled:opacity-40"
                      title={`View all orders placed by ${u.first_name} ${u.last_name}`}
                    >
                      <PackIcon className="w-3 h-3"/> Orders
                    </button>
                    <button
                      data-testid={`gift-btn-${u.id}`}
                      onClick={() => openGift(u)}
                      className="btn-outline-gold text-xs px-3 py-1 flex items-center gap-1"
                    >
                      <Gift className="w-3 h-3"/> Gift
                    </button>
                    <button
                      data-testid={`reset-offers-btn-${u.id}`}
                      onClick={async () => {
                        if (!window.confirm(`Reset Daily / Weekly / Monthly offer allowances for ${u.first_name} ${u.last_name}? This won't touch their order history — it just lets them buy those offers again in the current period.`)) return;
                        try {
                          setBusy(true);
                          const { data } = await api.post(`/admin/customers/${u.id}/reset-offers`);
                          const r = data.reset || {};
                          const total = (r.daily || 0) + (r.weekly || 0) + (r.monthly || 0);
                          toast.success(total > 0
                            ? `Reset — cleared ${total} order${total === 1 ? "" : "s"} (D:${r.daily || 0} · W:${r.weekly || 0} · M:${r.monthly || 0})`
                            : "Reset — customer had no active period usage");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Failed to reset offers");
                        } finally { setBusy(false); }
                      }}
                      disabled={busy}
                      className="btn-outline-gold text-xs px-3 py-1 flex items-center gap-1 disabled:opacity-40"
                      title="Reset Daily / Weekly / Monthly offer allowances for this customer"
                    >
                      <RotateCcw className="w-3 h-3"/> Reset Offers
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-[var(--av-text-dim)] py-6">No customers match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {viewOrders && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
             onClick={() => setViewOrders(null)} data-testid="customer-orders-modal">
          <div className="vault-card p-6 max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="mono-caption mb-1">Orders by</div>
                <h3 className="font-serif text-xl gold-text">
                  {viewOrders.customer?.first_name} {viewOrders.customer?.last_name}
                </h3>
                <p className="text-xs text-[var(--av-text-muted)] font-mono">
                  {viewOrders.customer?.id} · {viewOrders.customer?.email}
                </p>
                <p className="text-xs text-[var(--av-text-dim)] mt-1">
                  {viewOrders.total} order{viewOrders.total === 1 ? "" : "s"} · newest first
                </p>
              </div>
              <button onClick={() => setViewOrders(null)}
                      data-testid="customer-orders-close"
                      className="text-[var(--av-text-dim)] hover:text-[var(--av-gold)] text-xl leading-none">×</button>
            </div>
            <div className="overflow-auto flex-1">
              <table className="vault-table text-xs">
                <thead><tr>
                  <th>Order</th><th>Date</th><th>Product</th><th>Apex</th>
                  <th>Cash</th><th>Method</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {viewOrders.orders.map(o => (
                    <tr key={o.id} data-testid={`co-row-${o.id}`}
                        className={o.manual_dispatch ? "bg-[rgba(148,0,211,0.05)]" : ""}>
                      <td className="font-mono">
                        {o.id}
                        {o.manual_dispatch && <div className="text-[10px] purple-text">MANUAL</div>}
                      </td>
                      <td>{(o.paid_at || o.created_at || "").slice(0,16).replace("T"," ") || "N/A"}</td>
                      <td className="truncate max-w-[180px]">{o.package_snapshot?.name || "N/A"}</td>
                      <td>{o.package_snapshot?.apex_amount?.toLocaleString() || "N/A"}</td>
                      <td>£{Number(o.cash_to_pay || 0).toFixed(2)}</td>
                      <td>{o.payment_method || "N/A"}</td>
                      <td><span className="pill pill-gold text-[10px]">{o.status}</span></td>
                    </tr>
                  ))}
                  {viewOrders.orders.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-6 text-[var(--av-text-dim)]">
                      This customer hasn't placed any orders yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {gift && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
             onClick={() => !busy && setGift(null)} data-testid="gift-modal">
          <div className="vault-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="mono-caption mb-1">Gift Apex Rewards</div>
            <h3 className="font-serif text-xl gold-text mb-1">{gift.first_name} {gift.last_name}</h3>
            <p className="text-xs text-[var(--av-text-muted)] font-mono">{gift.id}</p>
            <p className="text-xs text-[var(--av-text-dim)] mt-1 mb-4">
              Current balance: <span className="purple-text font-semibold">£{gift.reward_available?.toFixed(2)}</span>
            </p>

            <label className="block text-xs text-[var(--av-text-dim)] mb-1">Amount (£)</label>
            <input
              data-testid="gift-amount"
              type="number" min="0.01" step="0.01" placeholder="5.00"
              className="input-vault mb-3"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />

            <label className="block text-xs text-[var(--av-text-dim)] mb-1">
              Reason (shown to the customer in their email)
            </label>
            <textarea
              data-testid="gift-reason"
              rows={3}
              className="input-vault mb-4"
              placeholder="e.g. Thank you for being a loyal Vault customer"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />

            <div className="flex justify-end gap-2">
              <button
                data-testid="gift-cancel"
                onClick={() => setGift(null)}
                disabled={busy}
                className="btn-outline-gold text-sm"
              >Cancel</button>
              <button
                data-testid="gift-submit"
                onClick={submitGift}
                disabled={busy}
                className="btn-gold text-sm"
              >{busy ? "Sending…" : "Send Gift"}</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function Catalogue() {
  const [sub, setSub] = useState("apex");
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button data-testid="cat-tab-apex" onClick={()=>setSub("apex")} className={`tab-btn ${sub==="apex"?"active":""}`}>Apex Products</button>
        <button data-testid="cat-tab-empire" onClick={()=>setSub("empire")} className={`tab-btn ${sub==="empire"?"active":""}`}>Empire Coins</button>
      </div>
      {sub === "apex" ? <ApexAdmin/> : <EmpireAdmin/>}
    </div>
  );
}

const DEFAULT_TIERS = [
  { min_qty: 1,   max_qty: 4,   unit_price_gbp: 0.99, label: "STANDARD" },
  { min_qty: 5,   max_qty: 9,   unit_price_gbp: 0.97, label: "BULK" },
  { min_qty: 10,  max_qty: 19,  unit_price_gbp: 0.95, label: "POPULAR" },
  { min_qty: 20,  max_qty: 49,  unit_price_gbp: 0.93, label: "BETTER VALUE" },
  { min_qty: 50,  max_qty: 99,  unit_price_gbp: 0.91, label: "BEST VALUE" },
  { min_qty: 100, max_qty: 100, unit_price_gbp: 0.89, label: "MAXIMUM VALUE" },
];

function ApexAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => api.get("/admin/apex-products").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);
  const archive = async (id) => {
    if (!window.confirm("Archive this Apex product? It will disappear from the store.")) return;
    try { await api.delete(`/admin/apex-products/${id}`); toast.success("Archived"); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title={<span className="flex justify-between items-center">Apex Products
      <button data-testid="apex-create-btn" onClick={()=>setShowCreate(true)} className="btn-gold text-xs">+ New Apex Product</button>
    </span>}>
      <table className="vault-table"><thead><tr><th>Name</th><th>Apex / pack</th><th>Tiers (1→100)</th><th>Status</th><th>Sort</th><th></th></tr></thead>
        <tbody>{rows.map(p=>(<tr key={p.id}>
          <td><div className="font-semibold">{p.name}</div><div className="text-xs text-[var(--av-text-dim)] font-mono">{p.id}</div></td>
          <td>{p.apex_amount?.toLocaleString()}</td>
          <td className="text-xs">{(p.tiers||[]).map(t=>(<div key={t.min_qty}>{t.min_qty}-{t.max_qty}: <span className="gold-text">£{Number(t.unit_price_gbp).toFixed(2)}</span> {t.label && <span className="text-[var(--av-text-dim)]">· {t.label}</span>}</div>))}</td>
          <td><span className={`pill ${p.active?"pill-gold":"pill-purple"}`}>{p.active?"Active":"Archived"}</span></td>
          <td>{p.sort_order}</td>
          <td className="whitespace-nowrap">
            <button data-testid={`apex-edit-${p.id}`} onClick={()=>setEditing(p)} className="btn-outline-gold text-xs mr-1">Edit</button>
            {p.active && <button data-testid={`apex-archive-${p.id}`} onClick={()=>archive(p.id)} className="btn-outline-gold text-xs">Archive</button>}
          </td>
        </tr>))}
        {rows.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-[var(--av-text-dim)]">No Apex products yet. Click + New Apex Product.</td></tr>}
        </tbody></table>
      {editing && <ApexEditor product={editing} onClose={()=>{ setEditing(null); load(); }}/>}
      {showCreate && <ApexEditor product={null} onClose={()=>{ setShowCreate(false); load(); }}/>}
    </Card>
  );
}

function ApexEditor({ product, onClose }) {
  const isNew = !product;
  const [f, setF] = useState(product ? {
    name: product.name, apex_amount: product.apex_amount,
    supplier_cost_usd: product.supplier_cost_usd || 0,
    tiers: product.tiers || DEFAULT_TIERS,
    sort_order: product.sort_order || 0, active: !!product.active,
  } : {
    name: "", apex_amount: 99, supplier_cost_usd: 0.48,
    tiers: JSON.parse(JSON.stringify(DEFAULT_TIERS)), sort_order: 10, active: true,
  });
  const [busy, setBusy] = useState(false);
  const setTier = (i, key, val) => { const t=[...f.tiers]; t[i]={...t[i], [key]: key==="label"?val:Number(val)}; setF({...f, tiers: t}); };
  const addTier = () => setF({...f, tiers:[...f.tiers, { min_qty:1, max_qty:1, unit_price_gbp:0.99, label:"" }]});
  const rmTier = (i) => setF({...f, tiers: f.tiers.filter((_,x)=>x!==i)});
  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try {
      if (isNew) await api.post("/admin/apex-products", f);
      else await api.put(`/admin/apex-products/${product.id}`, f);
      toast.success(isNew?"Created":"Saved"); onClose();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="vault-card p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="mono-caption mb-2">{isNew?"New Apex Product":"Edit Apex Product"}</div>
        <h3 className="font-serif text-2xl gold-text mb-4">{product?.name || "New product"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <label className="text-xs">Display name<input required data-testid="apex-name" className="input-vault mt-1" value={f.name} onChange={e=>setF({...f, name:e.target.value})} placeholder="Apex 99"/></label>
            <label className="text-xs">Apex per pack<input required type="number" data-testid="apex-amount" className="input-vault mt-1" value={f.apex_amount} onChange={e=>setF({...f, apex_amount:parseInt(e.target.value)||0})}/></label>
            <label className="text-xs">Supplier cost (USD/pack)<input required type="number" step="0.01" data-testid="apex-cost" className="input-vault mt-1" value={f.supplier_cost_usd} onChange={e=>setF({...f, supplier_cost_usd:parseFloat(e.target.value)||0})}/></label>
            <label className="text-xs">Sort order<input type="number" data-testid="apex-sort" className="input-vault mt-1" value={f.sort_order} onChange={e=>setF({...f, sort_order:parseInt(e.target.value)||0})}/></label>
            <label className="text-xs flex items-center gap-2 pt-4"><input type="checkbox" data-testid="apex-active" checked={f.active} onChange={e=>setF({...f, active:e.target.checked})}/> Active (visible in store)</label>
          </div>
          <div>
            <div className="mono-caption mb-2 flex justify-between items-center">Bulk Tiers (quantity → unit price)
              <button type="button" onClick={addTier} className="btn-outline-gold text-xs">+ Add Tier</button>
            </div>
            <table className="vault-table text-xs"><thead><tr><th>Min qty</th><th>Max qty</th><th>£ / pack</th><th>Label</th><th></th></tr></thead><tbody>
              {f.tiers.map((t,i)=>(<tr key={`tier-${t.min_qty}-${t.max_qty}-${i}`}>
                <td><input type="number" min="1" max="100" data-testid={`tier-min-${i}`} className="input-vault w-20" value={t.min_qty} onChange={e=>setTier(i,"min_qty",e.target.value)}/></td>
                <td><input type="number" min="1" max="100" data-testid={`tier-max-${i}`} className="input-vault w-20" value={t.max_qty} onChange={e=>setTier(i,"max_qty",e.target.value)}/></td>
                <td><input type="number" step="0.01" min="0.01" data-testid={`tier-price-${i}`} className="input-vault w-24" value={t.unit_price_gbp} onChange={e=>setTier(i,"unit_price_gbp",e.target.value)}/></td>
                <td><input data-testid={`tier-label-${i}`} className="input-vault" value={t.label||""} onChange={e=>setTier(i,"label",e.target.value)} placeholder="BULK"/></td>
                <td><button type="button" onClick={()=>rmTier(i)} className="text-[var(--av-purple)] text-xs">Remove</button></td>
              </tr>))}
            </tbody></table>
            <p className="text-[11px] text-[var(--av-text-dim)] mt-2">Tiers must cover ranges within 1..100. First-tier min should be 1; last-tier max should be 100. Prices should decrease as quantity grows.</p>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button disabled={busy} data-testid="apex-save" className="btn-gold text-sm flex-1">{saveBtnLabel(busy, isNew)}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmpireAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => api.get("/admin/empire-products").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);
  const archive = async id => { if (!window.confirm("Archive this Empire product?")) return;
    try { await api.delete(`/admin/empire-products/${id}`); toast.success("Archived"); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title={<span className="flex justify-between items-center">Empire Coins
      <button data-testid="empire-create-btn" onClick={()=>setShowCreate(true)} className="btn-gold text-xs">+ New Empire Product</button>
    </span>}>
      <table className="vault-table"><thead><tr><th>Name</th><th>Coins</th><th>Price</th><th>Supplier USD</th><th>Label</th><th>Status</th><th>Sort</th><th></th></tr></thead>
        <tbody>{rows.map(p=>(<tr key={p.id}>
          <td><div className="font-semibold">{p.name}</div><div className="text-xs text-[var(--av-text-dim)] font-mono">{p.id}</div></td>
          <td>{p.coin_amount?.toLocaleString()}</td>
          <td>£{Number(p.price_gbp||0).toFixed(2)}</td>
          <td>${Number(p.supplier_cost_usd||0).toFixed(2)}</td>
          <td>{p.label||"—"}</td>
          <td><span className={`pill ${p.active?"pill-gold":"pill-purple"}`}>{p.active?"Active":"Archived"}</span></td>
          <td>{p.sort_order}</td>
          <td className="whitespace-nowrap">
            <button data-testid={`empire-edit-${p.id}`} onClick={()=>setEditing(p)} className="btn-outline-gold text-xs mr-1">Edit</button>
            {p.active && <button data-testid={`empire-archive-${p.id}`} onClick={()=>archive(p.id)} className="btn-outline-gold text-xs">Archive</button>}
          </td>
        </tr>))}
        {rows.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-[var(--av-text-dim)]">No Empire products yet. Click + New Empire Product.</td></tr>}
        </tbody></table>
      {editing && <EmpireEditor product={editing} onClose={()=>{ setEditing(null); load(); }}/>}
      {showCreate && <EmpireEditor product={null} onClose={()=>{ setShowCreate(false); load(); }}/>}
    </Card>
  );
}

function EmpireEditor({ product, onClose }) {
  const isNew = !product;
  const [f, setF] = useState(product ? {
    name: product.name, coin_amount: product.coin_amount, price_gbp: product.price_gbp,
    supplier_cost_usd: product.supplier_cost_usd || 0, sort_order: product.sort_order || 0,
    active: !!product.active, label: product.label || "",
  } : { name:"", coin_amount:100, price_gbp:0.99, supplier_cost_usd:0.5, sort_order:10, active:true, label:"" });
  const [busy, setBusy] = useState(false);
  const submit = async e => { e.preventDefault(); setBusy(true);
    try {
      if (isNew) await api.post("/admin/empire-products", f);
      else await api.put(`/admin/empire-products/${product.id}`, f);
      toast.success(isNew?"Created":"Saved"); onClose();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="vault-card p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
        <div className="mono-caption mb-2">{isNew?"New Empire Product":"Edit Empire Product"}</div>
        <h3 className="font-serif text-2xl gold-text mb-4">{product?.name || "New product"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-xs block">Name<input required data-testid="empire-name" className="input-vault mt-1" value={f.name} onChange={e=>setF({...f, name:e.target.value})}/></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">Coin amount<input required type="number" data-testid="empire-coins" className="input-vault mt-1" value={f.coin_amount} onChange={e=>setF({...f, coin_amount:parseInt(e.target.value)||0})}/></label>
            <label className="text-xs">Price (£)<input required type="number" step="0.01" data-testid="empire-price" className="input-vault mt-1" value={f.price_gbp} onChange={e=>setF({...f, price_gbp:parseFloat(e.target.value)||0})}/></label>
            <label className="text-xs">Supplier cost (USD)<input required type="number" step="0.01" className="input-vault mt-1" value={f.supplier_cost_usd} onChange={e=>setF({...f, supplier_cost_usd:parseFloat(e.target.value)||0})}/></label>
            <label className="text-xs">Sort order<input type="number" className="input-vault mt-1" value={f.sort_order} onChange={e=>setF({...f, sort_order:parseInt(e.target.value)||0})}/></label>
          </div>
          <label className="text-xs block">Label (optional)<input data-testid="empire-label" className="input-vault mt-1" value={f.label} onChange={e=>setF({...f, label:e.target.value})} placeholder="POPULAR / BEST VALUE"/></label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={e=>setF({...f, active:e.target.checked})}/> Active (visible in store)</label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button disabled={busy} data-testid="empire-save" className="btn-gold text-sm flex-1">{saveBtnLabel(busy, isNew)}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Offers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => api.get("/admin/vault-offers").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);
  const toggle = async (o) => {
    const nextStatus = o.status === "active" ? "inactive" : "active";
    try { await api.put(`/admin/vault-offers/${o.id}`, {...o, status: nextStatus}); toast.success(nextStatus==="active"?"Offer live at checkout":"Offer hidden"); load(); }
    catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  // Predict which offers /checkout/quote will stack right now. Every active
  // offer within its start/end window is applied — customers get the sum of
  // all percentages plus the sum of all fixed £ amounts.
  const nowIso = new Date().toISOString();
  const activeOffers = rows.filter(o =>
    o.status === "active"
    && (!o.starts_at || o.starts_at <= nowIso)
    && (!o.ends_at || o.ends_at >= nowIso)
  ).sort((a,b) => {
    const sa = (Number(a.reward_percentage)||0) + (Number(a.reward_fixed_gbp)||0);
    const sb = (Number(b.reward_percentage)||0) + (Number(b.reward_fixed_gbp)||0);
    if (sb !== sa) return sb - sa;
    return String(b.created_at||"").localeCompare(String(a.created_at||""));
  });
  const activeIdSet = new Set(activeOffers.map(o => o.id));
  const stackTotalPct = activeOffers.reduce((s, o) => s + (Number(o.reward_percentage)||0), 0);
  const stackTotalFixed = activeOffers.reduce((s, o) => s + (Number(o.reward_fixed_gbp)||0), 0);
  const del = async (id) => {
    if (!window.confirm("Delete this offer permanently? Customers will no longer see it.")) return;
    try { await api.delete(`/admin/vault-offers/${id}`); toast.success("Deleted"); load(); }
    catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title={<span className="flex justify-between items-center">Vault Offers
      <button data-testid="offer-create-btn" onClick={()=>setShowCreate(true)} className="btn-gold text-xs">+ New Offer</button>
    </span>}>
      <div className="text-[11px] text-[var(--av-text-dim)] mb-3">
        {activeOffers.length === 0 && <>No active vault offer will apply at checkout right now. Set an offer to <b>Active</b> to make it live.</>}
        {activeOffers.length === 1 && <>Currently live at checkout: <span className="gold-text font-semibold">✓ {activeOffers[0].name} (+{activeOffers[0].reward_percentage}%{activeOffers[0].reward_fixed_gbp>0?` + £${Number(activeOffers[0].reward_fixed_gbp).toFixed(2)}`:""})</span>. Toggle to Inactive to hide.</>}
        {activeOffers.length > 1 && <>Stacking {activeOffers.length} offers at checkout: <span className="gold-text font-semibold">+{stackTotalPct.toFixed(2)}%{stackTotalFixed>0?` + £${stackTotalFixed.toFixed(2)}`:""}</span> combined. All active offers apply together — deactivate any you don't want to stack.</>}
      </div>
      <table className="vault-table"><thead><tr><th>Name</th><th>Type</th><th>Value</th><th>Redemptions</th><th>Status</th><th></th></tr></thead>
        <tbody>{rows.map(o=>{
          const isWinner = activeIdSet.has(o.id);
          return (<tr key={o.id} className={isWinner?"ring-1 ring-[var(--av-gold)]/60":""}>
          <td><div className="font-semibold flex items-center gap-2">{o.name}{isWinner && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--av-gold)]/15 text-[var(--av-gold)]">LIVE @ CHECKOUT</span>}</div><div className="text-xs text-[var(--av-text-dim)]">{o.description}</div></td>
          <td>{o.reward_type}</td>
          <td className="gold-text font-semibold">{o.reward_type==="percentage"?`+${o.reward_percentage}%`:`£${Number(o.reward_fixed_gbp||0).toFixed(2)}`}</td>
          <td className="text-xs">{o.redemption_count||0}{o.total_redemptions?` / ${o.total_redemptions}`:""}</td>
          <td><span className={`pill ${o.status==="active"?"pill-gold":"pill-purple"}`}>{o.status}</span></td>
          <td className="whitespace-nowrap">
            <button data-testid={`offer-toggle-${o.id}`} onClick={()=>toggle(o)} className="btn-outline-gold text-xs mr-1">{o.status==="active"?"Deactivate":"Activate"}</button>
            <button data-testid={`offer-edit-${o.id}`} onClick={()=>setEditing(o)} className="btn-outline-gold text-xs mr-1">Edit</button>
            <button data-testid={`offer-delete-${o.id}`} onClick={()=>del(o.id)} className="btn-outline-gold text-xs">Delete</button>
          </td>
        </tr>);})}
        {rows.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-[var(--av-text-dim)]">No vault offers yet. Click + New Offer to create one.</td></tr>}
        </tbody></table>
      {editing && <OfferEditor offer={editing} onClose={()=>{ setEditing(null); load(); }}/>}
      {showCreate && <OfferEditor offer={null} onClose={()=>{ setShowCreate(false); load(); }}/>}
    </Card>
  );
}

function OfferEditor({ offer, onClose }) {
  const isNew = !offer;
  const [f, setF] = useState(offer ? {
    name: offer.name, description: offer.description, reward_type: offer.reward_type,
    reward_percentage: offer.reward_percentage || 0, reward_fixed_gbp: offer.reward_fixed_gbp || 0,
    starts_at: offer.starts_at || null, ends_at: offer.ends_at || null,
    total_redemptions: offer.total_redemptions || null,
    per_customer_limit: offer.per_customer_limit || 1,
    eligible_package_ids: offer.eligible_package_ids || [],
    eligible_tiers: offer.eligible_tiers || [], status: offer.status || "active",
  } : { name:"", description:"", reward_type:"percentage", reward_percentage:1, reward_fixed_gbp:0,
        starts_at:null, ends_at:null, total_redemptions:null, per_customer_limit:1,
        eligible_package_ids:[], eligible_tiers:[], status:"active" });
  const [busy, setBusy] = useState(false);
  const submit = async e => { e.preventDefault(); setBusy(true);
    try {
      if (isNew) await api.post("/admin/vault-offers", f);
      else await api.put(`/admin/vault-offers/${offer.id}`, f);
      toast.success(isNew?"Offer created":"Saved"); onClose();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="vault-card p-6 max-w-md w-full" onClick={e=>e.stopPropagation()}>
        <div className="mono-caption mb-2">{isNew?"New Vault Offer":"Edit Offer"}</div>
        <h3 className="font-serif text-2xl gold-text mb-4">{offer?.name || "New offer"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-xs block">Name<input required data-testid="offer-name" className="input-vault mt-1" value={f.name} onChange={e=>setF({...f, name:e.target.value})}/></label>
          <label className="text-xs block">Description<textarea required rows="2" data-testid="offer-desc" className="input-vault mt-1" value={f.description} onChange={e=>setF({...f, description:e.target.value})}/></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">Reward type<select data-testid="offer-type" className="input-vault mt-1" value={f.reward_type} onChange={e=>setF({...f, reward_type:e.target.value})}><option value="percentage">Percentage bonus</option><option value="fixed">Fixed £</option></select></label>
            {f.reward_type==="percentage"
              ? <label className="text-xs">Bonus %<input type="number" step="0.01" min="0" data-testid="offer-pct" className="input-vault mt-1" value={f.reward_percentage} onChange={e=>setF({...f, reward_percentage:parseFloat(e.target.value)||0})}/></label>
              : <label className="text-xs">Fixed £<input type="number" step="0.01" data-testid="offer-fixed" className="input-vault mt-1" value={f.reward_fixed_gbp} onChange={e=>setF({...f, reward_fixed_gbp:parseFloat(e.target.value)||0})}/></label>}
            <label className="text-xs">Per-customer limit<input type="number" className="input-vault mt-1" value={f.per_customer_limit||""} onChange={e=>setF({...f, per_customer_limit:parseInt(e.target.value)||null})}/></label>
            <label className="text-xs">Total redemptions cap<input type="number" className="input-vault mt-1" placeholder="Unlimited" value={f.total_redemptions||""} onChange={e=>setF({...f, total_redemptions:parseInt(e.target.value)||null})}/></label>
            <label className="text-xs">Starts at (ISO)<input type="datetime-local" className="input-vault mt-1" value={f.starts_at?f.starts_at.slice(0,16):""} onChange={e=>setF({...f, starts_at: e.target.value?new Date(e.target.value).toISOString():null})}/></label>
            <label className="text-xs">Ends at (ISO)<input type="datetime-local" className="input-vault mt-1" value={f.ends_at?f.ends_at.slice(0,16):""} onChange={e=>setF({...f, ends_at: e.target.value?new Date(e.target.value).toISOString():null})}/></label>
            <label className="text-xs">Status<select data-testid="offer-status" className="input-vault mt-1" value={f.status==="active"?"active":"inactive"} onChange={e=>setF({...f, status:e.target.value})}><option value="active">Active — applies at checkout</option><option value="inactive">Inactive — hidden</option></select></label>
          </div>
          <p className="text-[11px] text-[var(--av-text-dim)]">Creating an active offer sends a launch email to every verified customer who opted in to marketing.</p>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button disabled={busy} data-testid="offer-save" className="btn-gold text-sm flex-1">{saveBtnLabel(busy, isNew, "Create Offer")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EventBoardAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => api.get("/admin/event-board").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (o) => {
    try {
      await api.put(`/admin/event-board/${o.id}`, { ...o, active: !o.active });
      toast.success(o.active ? "Event hidden" : "Event live");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this event permanently?")) return;
    try { await api.delete(`/admin/event-board/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title={<span className="flex justify-between items-center">Home Event Board
      <button data-testid="event-create-btn" onClick={() => setShowCreate(true)} className="btn-gold text-xs">+ New Event</button>
    </span>}>
      <p className="text-[11px] text-[var(--av-text-dim)] mb-3">Rotating carousel shown on the Home page between the Vault hero and the Apex packages. The whole section hides automatically when no events are active.</p>
      {/* Mobile — card view */}
      <div className="sm:hidden space-y-3">
        {rows.map(o => (
          <div key={o.id} className="rounded-lg border border-[var(--av-border)] p-3 bg-[rgba(11,12,16,0.5)]">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="font-semibold text-sm">{o.title}</div>
              <span className={`pill ${o.active ? "pill-gold" : "pill-purple"} text-[10px]`}>{o.active ? "Live" : "Hidden"}</span>
            </div>
            <div className="text-xs text-[var(--av-text-dim)] mb-2 line-clamp-2">{o.body}</div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--av-text-dim)] mb-2">
              <span>Cashback: <span className="gold-text font-semibold">{o.cashback_percent ? `${o.cashback_percent}%` : "—"}</span></span>
              <span>Button: {o.cta_label || "—"}</span>
              <span>Sort: {o.sort_order || 0}</span>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`event-toggle-${o.id}-m`} onClick={() => toggle(o)} className="btn-outline-gold text-xs flex-1">{o.active ? "Hide" : "Show"}</button>
              <button data-testid={`event-edit-${o.id}-m`} onClick={() => setEditing(o)} className="btn-outline-gold text-xs flex-1">Edit</button>
              <button data-testid={`event-delete-${o.id}-m`} onClick={() => del(o.id)} className="btn-outline-gold text-xs flex-1">Delete</button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-center py-8 text-[var(--av-text-dim)] text-sm">No events yet. Click + New Event to add one.</div>}
      </div>
      {/* Desktop — table view */}
      <div className="hidden sm:block overflow-x-auto">
      <table className="vault-table"><thead><tr>
        <th>Title</th><th>Body</th><th>Cashback</th><th>Button</th><th>Sort</th><th>Status</th><th></th>
      </tr></thead><tbody>
        {rows.map(o => (
          <tr key={o.id}>
            <td className="font-semibold">{o.title}</td>
            <td className="text-xs text-[var(--av-text-dim)] max-w-md truncate">{o.body}</td>
            <td className="gold-text font-semibold text-xs">{o.cashback_percent ? `${o.cashback_percent}%` : "—"}</td>
            <td className="text-xs">{o.cta_label ? <span title={o.cta_href}>{o.cta_label}</span> : "—"}</td>
            <td className="text-xs">{o.sort_order || 0}</td>
            <td><span className={`pill ${o.active ? "pill-gold" : "pill-purple"}`}>{o.active ? "Live" : "Hidden"}</span></td>
            <td className="whitespace-nowrap">
              <button data-testid={`event-toggle-${o.id}`} onClick={() => toggle(o)} className="btn-outline-gold text-xs mr-1">{o.active ? "Hide" : "Show"}</button>
              <button data-testid={`event-edit-${o.id}`} onClick={() => setEditing(o)} className="btn-outline-gold text-xs mr-1">Edit</button>
              <button data-testid={`event-delete-${o.id}`} onClick={() => del(o.id)} className="btn-outline-gold text-xs">Delete</button>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-[var(--av-text-dim)]">No events yet. Click + New Event to add one.</td></tr>}
      </tbody></table>
      </div>
      {editing && <EventEditor event={editing} onClose={() => { setEditing(null); load(); }} />}
      {showCreate && <EventEditor event={null} onClose={() => { setShowCreate(false); load(); }} />}
    </Card>
  );
}

function EventEditor({ event, onClose }) {
  const isNew = !event;
  const [f, setF] = useState(event ? {
    title: event.title, body: event.body,
    cashback_percent: event.cashback_percent ?? null,
    cta_label: event.cta_label || "", cta_href: event.cta_href || "",
    active: !!event.active, sort_order: event.sort_order || 0,
  } : { title: "", body: "", cashback_percent: null, cta_label: "", cta_href: "", active: true, sort_order: 0 });
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        ...f,
        cashback_percent: f.cashback_percent === "" || f.cashback_percent === null ? null : parseFloat(f.cashback_percent),
        cta_label: f.cta_label?.trim() || null,
        cta_href: f.cta_href?.trim() || null,
      };
      if (isNew) await api.post("/admin/event-board", payload);
      else await api.put(`/admin/event-board/${event.id}`, payload);
      toast.success(isNew ? "Event created" : "Saved");
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="vault-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="mono-caption mb-2">{isNew ? "New Event" : "Edit Event"}</div>
        <h3 className="font-serif text-2xl gold-text mb-4">{event?.title || "New event"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-xs block">Title
            <input required data-testid="event-title" className="input-vault mt-1" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} />
          </label>
          <label className="text-xs block">Body
            <textarea required rows="4" data-testid="event-body" className="input-vault mt-1" value={f.body} onChange={e => setF({ ...f, body: e.target.value })} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">Cashback % (optional)
              <input type="number" step="0.5" placeholder="e.g. 10" data-testid="event-cashback" className="input-vault mt-1"
                value={f.cashback_percent ?? ""}
                onChange={e => setF({ ...f, cashback_percent: e.target.value === "" ? null : parseFloat(e.target.value) })} />
            </label>
            <label className="text-xs">Sort order
              <input type="number" data-testid="event-sort" className="input-vault mt-1"
                value={f.sort_order}
                onChange={e => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">Button label (optional)
              <input type="text" placeholder="e.g. Contact Support" data-testid="event-cta-label" className="input-vault mt-1"
                value={f.cta_label} onChange={e => setF({ ...f, cta_label: e.target.value })} />
            </label>
            <label className="text-xs">Button link
              <input type="text" placeholder="/my-vault/support or https://…" data-testid="event-cta-href" className="input-vault mt-1"
                value={f.cta_href} onChange={e => setF({ ...f, cta_href: e.target.value })} />
            </label>
          </div>
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" data-testid="event-active" checked={f.active} onChange={e => setF({ ...f, active: e.target.checked })} />
            <span>Show on Home page</span>
          </label>
          <p className="text-[11px] text-[var(--av-text-dim)]">Leave cashback % blank to omit the badge. Both button fields must be filled to show a CTA. Internal paths like <code>/my-vault/support</code> open in-app; full https URLs open in a new tab. Lowest sort order shows first.</p>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button disabled={busy} data-testid="event-save" className="btn-gold text-sm flex-1">{busy ? "Saving…" : (isNew ? "Create Event" : "Save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LoyaltyAdmin() {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get("/loyalty/tiers").then(r=>setRows(r.data)); },[]);
  const save = async t => { await api.put(`/admin/loyalty/${t.id}`, { name:t.name, min_spend:t.min_spend, max_spend:t.max_spend, reward_rate:t.reward_rate, order:t.order, color:t.color }); toast.success("Saved"); };
  return (<Card title="Loyalty Tiers"><table className="vault-table"><thead><tr><th>Name</th><th>Min Spend</th><th>Max Spend</th><th>Rate %</th><th></th></tr></thead><tbody>{rows.map((t,i)=>(<tr key={t.id}><td>{t.name}</td><td><input className="input-vault" type="number" value={t.min_spend} onChange={e=>{ const c=[...rows]; c[i]={...t, min_spend: parseFloat(e.target.value)}; setRows(c); }}/></td><td><input className="input-vault" type="number" value={t.max_spend} onChange={e=>{ const c=[...rows]; c[i]={...t, max_spend: parseFloat(e.target.value)}; setRows(c); }}/></td><td><input className="input-vault" type="number" step="0.05" value={t.reward_rate} onChange={e=>{ const c=[...rows]; c[i]={...t, reward_rate: parseFloat(e.target.value)}; setRows(c); }}/></td><td><button onClick={()=>save(t)} className="btn-outline-gold text-xs">Save</button></td></tr>))}</tbody></table></Card>);
}

function Settings() {
  const [s, setS] = useState(null);
  useEffect(()=>{ api.get("/admin/settings").then(r=>setS(r.data)); },[]);
  if (!s) return <div className="py-10 text-center text-[var(--av-text-dim)] text-sm">Loading settings…</div>;
  const save = async e => { e.preventDefault(); await api.put("/admin/settings", s); toast.success("Settings saved"); };
  const num = k => <input type="number" step="0.01" className="input-vault" value={s[k]} onChange={e=>setS({...s, [k]: parseFloat(e.target.value)})}/>;
  return (<Card title="Business Settings"><form onSubmit={save} className="grid md:grid-cols-2 gap-3">
    <label className="text-xs">Store Status
      <select data-testid="store-status" className="input-vault" value={s.store_status} onChange={e=>setS({...s, store_status:e.target.value})}>
        <option value="open">Open — Sales Live</option>
        <option value="high_demand">Open — High Demand Notice</option>
        <option value="maintenance">Paused — Maintenance</option>
        <option value="closed">Paused — Sales Halted</option>
      </select>
      <span className="text-[10px] text-[var(--av-text-dim)] block mt-1">"Paused" states show a purple banner across the site and reject any new checkout with a friendly message.</span>
    </label>
    <label className="text-xs">Supplier Cost USD / 499 (owner-only){num("supplier_cost_usd_per_499")}</label>
    <label className="text-xs">FX USD→GBP{num("fx_usd_to_gbp")}</label>
    <label className="text-xs">Card fee %{num("payment_cost_percent_card")}</label>
    <label className="text-xs">Pay by Bank fee %{num("payment_cost_percent_pbb")}</label>
    <label className="text-xs">Pay by Bank bonus %{num("pay_by_bank_bonus_rate")}</label>
    <label className="text-xs">Referral bonus %{num("referral_bonus_rate")}</label>
    <label className="text-xs">Referrer rate %{num("referrer_rate")}</label>
    <label className="text-xs">Max redemption %{num("max_redemption_percent")}</label>
    <label className="text-xs">Min redemption £{num("min_redemption_gbp")}</label>
    <label className="text-xs">Max order value (£)
      <input type="number" step="1" data-testid="max-order-gbp" className="input-vault mt-1"
        value={s.max_order_gbp ?? 1000}
        onChange={e=>setS({...s, max_order_gbp: parseFloat(e.target.value)||0})}/>
      <span className="text-[10px] text-[var(--av-text-dim)] block mt-1">Hard cap on a single order's PRE-discount product total. Rejects checkout with a friendly message when breached. Default £1,000 — raise or lower to match your supplier wallet risk appetite.</span>
    </label>
    <label className="text-xs">Supplier wallet low alert ($)
      <input type="number" step="10" data-testid="supplier-wallet-threshold" className="input-vault mt-1"
        value={s.supplier_wallet_low_threshold_usd ?? 1000}
        onChange={e=>setS({...s, supplier_wallet_low_threshold_usd: parseFloat(e.target.value)||0})}/>
      <span className="text-[10px] text-[var(--av-text-dim)] block mt-1">Below this OLD STORE wallet balance you'll get a "⚠️ SUPPLIER WALLET LOW" Telegram. Checked every 15 min automatically. A one-shot "✅ RESTORED" Telegram fires when the balance climbs back above.</span>
    </label>
    <label className="text-xs">Wallet alert cooldown (hours)
      <input type="number" step="0.5" data-testid="supplier-wallet-cooldown" className="input-vault mt-1"
        value={s.supplier_wallet_alert_cooldown_hours ?? 6}
        onChange={e=>setS({...s, supplier_wallet_alert_cooldown_hours: parseFloat(e.target.value)||0})}/>
      <span className="text-[10px] text-[var(--av-text-dim)] block mt-1">Minimum hours between repeat low-balance Telegrams so you're not spammed. Default 6h.</span>
    </label>
    <label className="text-xs">Auto-confirm days<input type="number" className="input-vault" value={s.auto_confirm_days} onChange={e=>setS({...s, auto_confirm_days:parseInt(e.target.value)})}/></label>
    <label className="text-xs flex items-start gap-2 col-span-full mt-2 p-3 border border-red-500/30 rounded bg-red-500/5">
      <input type="checkbox" data-testid="auto-credit-rewards-toggle"
        checked={!!s.auto_credit_rewards_on_supplier_completion}
        onChange={e=>setS({...s, auto_credit_rewards_on_supplier_completion: e.target.checked})}
        className="mt-1" disabled/>
      <span className="flex-1 opacity-60">
        <span className="block font-semibold">Auto-credit on supplier completion — <span className="text-red-400">DEPRECATED</span></span>
        <span className="block text-[10px] text-[var(--av-text-dim)] mt-1 leading-snug">
          As of 2026-02 this setting no longer has any effect. Apex Rewards + referrer credit are ALWAYS released the moment the payment succeeds, regardless of when the supplier confirms fulfilment. Refunds still claw both sides back proportionally. Leaving the checkbox here for historical clarity; will be removed in a future release.
        </span>
      </span>
    </label>
    <div className="md:col-span-2"><button data-testid="save-settings" className="btn-gold">Save</button></div>
  </form></Card>);
}

function PromoCodes() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const load = () => api.get("/admin/promo-codes").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);
  const del = async (p) => {
    if (!window.confirm(`Delete promo code "${p.code}"? Customers using it will get "code not found".`)) return;
    try { await api.delete(`/admin/promo-codes/${p.id}`); toast.success("Deleted"); load(); }
    catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const toggle = async (p) => {
    try { await api.put(`/admin/promo-codes/${p.id}`, {...p, active: !p.active}); toast.success(p.active ? "Paused" : "Reactivated"); load(); }
    catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title={<span className="flex justify-between items-center">Promo Codes
      <button data-testid="promo-create-btn" onClick={()=>setShowCreate(true)} className="btn-gold text-xs">+ New Promo Code</button>
    </span>}>
      <table className="vault-table"><thead><tr>
        <th>Code</th><th>Discount</th><th>Uses</th><th>Per customer</th><th>Applies to</th><th>Status</th><th></th>
      </tr></thead><tbody>{rows.map(p=>(
        <tr key={p.id}>
          <td>
            <div className="font-mono font-bold gold-text">{p.code}</div>
            {p.description && <div className="text-xs text-[var(--av-text-dim)]">{p.description}</div>}
          </td>
          <td>{p.discount_type==="percentage" ? `${p.discount_value}%` : `£${Number(p.discount_value||0).toFixed(2)}`}</td>
          <td>{p.uses_count||0}{p.max_uses ? ` / ${p.max_uses}` : " (unlimited)"}</td>
          <td>{p.per_customer_limit}</td>
          <td className="text-xs">{p.applies_to}</td>
          <td><span className={`pill ${p.active?"pill-gold":"pill-purple"}`}>{p.active?"Active":"Paused"}</span></td>
          <td className="whitespace-nowrap">
            <button data-testid={`promo-toggle-${p.id}`} onClick={()=>toggle(p)} className="btn-outline-gold text-xs mr-1">{p.active?"Pause":"Resume"}</button>
            <button data-testid={`promo-edit-${p.id}`} onClick={()=>setEditing(p)} className="btn-outline-gold text-xs mr-1">Edit</button>
            <button data-testid={`promo-delete-${p.id}`} onClick={()=>del(p)} className="btn-outline-gold text-xs">Delete</button>
          </td>
        </tr>
      ))}
      {rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-[var(--av-text-dim)]">No promo codes yet. Click + New Promo Code to create one.</td></tr>}
      </tbody></table>
      {editing && <PromoEditor promo={editing} onClose={()=>{ setEditing(null); load(); }}/>}
      {showCreate && <PromoEditor promo={null} onClose={()=>{ setShowCreate(false); load(); }}/>}
    </Card>
  );
}

function PromoEditor({ promo, onClose }) {
  const isNew = !promo;
  const [f, setF] = useState(promo ? {
    code: promo.code, description: promo.description || "",
    discount_type: promo.discount_type, discount_value: promo.discount_value,
    max_uses: promo.max_uses, per_customer_limit: promo.per_customer_limit,
    min_order_gbp: promo.min_order_gbp, applies_to: promo.applies_to,
    starts_at: promo.starts_at || null, ends_at: promo.ends_at || null,
    active: !!promo.active,
  } : { code:"", description:"", discount_type:"percentage", discount_value:10,
        max_uses:null, per_customer_limit:1, min_order_gbp:0, applies_to:"all",
        starts_at:null, ends_at:null, active:true });
  const [busy, setBusy] = useState(false);
  const submit = async e => { e.preventDefault(); setBusy(true);
    try {
      const payload = {...f, code: (f.code||"").trim().toUpperCase()};
      if (isNew) await api.post("/admin/promo-codes", payload);
      else await api.put(`/admin/promo-codes/${promo.id}`, payload);
      toast.success(isNew?"Promo code created":"Saved"); onClose();
    } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="vault-card p-6 max-w-md w-full max-h-[92vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="mono-caption mb-2">{isNew?"New Promo Code":"Edit Promo Code"}</div>
        <h3 className="font-serif text-2xl gold-text mb-4">{promo?.code || "New promo"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <label className="text-xs block">Code (uppercase, no spaces)
            <input required data-testid="promo-code" className="input-vault mt-1 font-mono uppercase"
              value={f.code} onChange={e=>setF({...f, code:e.target.value.toUpperCase().replace(/\s/g,"")})}
              placeholder="LAUNCH20"/>
          </label>
          <label className="text-xs block">Internal description (shown to owner only)
            <input data-testid="promo-desc" className="input-vault mt-1"
              value={f.description} onChange={e=>setF({...f, description:e.target.value})}
              placeholder="Launch-day 20% off, capped at 500 uses"/>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">Discount type
              <select data-testid="promo-type" className="input-vault mt-1" value={f.discount_type}
                onChange={e=>setF({...f, discount_type:e.target.value})}>
                <option value="percentage">Percentage off</option>
                <option value="fixed">Fixed £ off</option>
              </select>
            </label>
            <label className="text-xs">{f.discount_type==="percentage" ? "Percent off (0–100)" : "£ off"}
              <input required type="number" step="0.01" data-testid="promo-value" className="input-vault mt-1"
                value={f.discount_value} onChange={e=>setF({...f, discount_value:parseFloat(e.target.value)||0})}/>
            </label>
            <label className="text-xs">Max total uses (blank = unlimited)
              <input type="number" data-testid="promo-maxuses" className="input-vault mt-1"
                placeholder="Unlimited" value={f.max_uses||""}
                onChange={e=>setF({...f, max_uses: e.target.value === "" ? null : parseInt(e.target.value)||null})}/>
            </label>
            <label className="text-xs">Per-customer limit
              <input type="number" data-testid="promo-perlimit" className="input-vault mt-1"
                value={f.per_customer_limit}
                onChange={e=>setF({...f, per_customer_limit:parseInt(e.target.value)||1})}/>
            </label>
            <label className="text-xs">Minimum order (£)
              <input type="number" step="0.01" data-testid="promo-minorder" className="input-vault mt-1"
                value={f.min_order_gbp}
                onChange={e=>setF({...f, min_order_gbp:parseFloat(e.target.value)||0})}/>
            </label>
            <label className="text-xs">Applies to
              <select data-testid="promo-applies" className="input-vault mt-1" value={f.applies_to}
                onChange={e=>setF({...f, applies_to:e.target.value})}>
                <option value="all">All products</option>
                <option value="apex">Apex only</option>
                <option value="empire">Empire only</option>
              </select>
            </label>
            <label className="text-xs">Starts at
              <input type="datetime-local" className="input-vault mt-1"
                value={f.starts_at?f.starts_at.slice(0,16):""}
                onChange={e=>setF({...f, starts_at: e.target.value?new Date(e.target.value).toISOString():null})}/>
            </label>
            <label className="text-xs">Ends at
              <input type="datetime-local" className="input-vault mt-1"
                value={f.ends_at?f.ends_at.slice(0,16):""}
                onChange={e=>setF({...f, ends_at: e.target.value?new Date(e.target.value).toISOString():null})}/>
            </label>
          </div>
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" data-testid="promo-active" checked={f.active}
              onChange={e=>setF({...f, active:e.target.checked})}/> Active (customers can use it)
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold text-sm flex-1">Cancel</button>
            <button disabled={busy} data-testid="promo-save" className="btn-gold text-sm flex-1">{saveBtnLabel(busy, isNew)}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SupportTickets() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const q = filter === "ALL" ? "" : `?status=${filter}`;
    api.get(`/admin/support/tickets${q}`).then(r => setRows(r.data));
  }, [filter]);
  const load = () => {
    const q = filter === "ALL" ? "" : `?status=${filter}`;
    api.get(`/admin/support/tickets${q}`).then(r => setRows(r.data));
  };
  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${selected.id}/reply`, { message: reply.trim() });
      toast.success("Reply sent to customer");
      setReply(""); setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const setStatus = async (tid, status) => {
    try {
      await api.post(`/admin/support/tickets/${tid}/status`, { status });
      toast.success(`Ticket ${status}`); load(); setSelected(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const pillClass = (s) => {
    if (s === "OPEN") return "pill-purple";
    if (s === "AWAITING CUSTOMER") return "pill-gold";
    return "pill-purple";
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 min-w-0">
        <Card title={<span className="flex justify-between items-center gap-2 flex-wrap">Support Tickets
          <select data-testid="support-filter" value={filter} onChange={e=>setFilter(e.target.value)} className="input-vault text-xs w-auto">
            <option value="ALL">All</option>
            <option value="OPEN">Open</option>
            <option value="AWAITING CUSTOMER">Awaiting Customer</option>
            <option value="RESOLVED">Resolved</option>
            <option value="CLOSED">Closed</option>
          </select>
        </span>}>
          <table className="vault-table">
            <thead><tr><th>Priority</th><th>Subject</th><th>Customer</th><th>Order</th><th>Status</th><th>Updated</th><th></th></tr></thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} className={selected?.id === t.id ? "bg-[rgba(212,175,55,0.08)]" : ""}>
                  <td>{t.priority === "high" ? <span className="pill pill-purple">HIGH</span> : <span className="text-xs">{t.priority}</span>}</td>
                  <td><div className="font-semibold">{t.subject}</div><div className="text-xs text-[var(--av-text-dim)] font-mono">{t.category}</div></td>
                  <td className="text-xs">{t.customer_name || "—"}<div className="text-[var(--av-text-dim)]">{t.customer_email}</div></td>
                  <td className="font-mono text-xs">{t.order_id || "—"}</td>
                  <td><span className={`pill ${pillClass(t.status)}`}>{t.status}</span></td>
                  <td className="text-xs">{t.updated_at?.slice(0,16).replace("T"," ")}</td>
                  <td><button data-testid={`support-open-${t.id}`} onClick={()=>{ setSelected(t); setReply(""); }} className="btn-outline-gold text-xs">Open</button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-[var(--av-text-dim)]">No tickets in this view.</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
      <div className="min-w-0">
        <Card title={selected ? `Ticket ${selected.id}` : "Select a ticket"}>
          {!selected && <div className="text-sm text-[var(--av-text-dim)] py-4">Click Open on any row to view messages and reply.</div>}
          {selected && (
            <div className="space-y-4">
              <div className="text-xs">
                <div className="font-semibold text-[var(--av-gold)]">{selected.subject}</div>
                <div className="text-[var(--av-text-dim)]">{selected.customer_name} · {selected.customer_email}</div>
                {selected.order_id && <div className="text-[var(--av-text-dim)] font-mono">Order {selected.order_id}</div>}
              </div>
              <div className="max-h-72 overflow-y-auto space-y-2 border border-[var(--av-border)] rounded-lg p-3 bg-[rgba(11,12,16,0.4)]">
                {(selected.messages || []).map((m, i) => (
                  <div key={m.at || `msg-${i}`} className={`text-xs ${m.from === "support" ? "text-[var(--av-gold)]" : "text-[var(--av-text)]"}`}>
                    <div className="font-mono text-[10px] text-[var(--av-text-dim)] mb-1">
                      {m.from === "support" ? "SUPPORT" : "CUSTOMER"} · {m.at?.slice(0,16).replace("T"," ")}
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed">{m.message}</div>
                  </div>
                ))}
              </div>
              <textarea
                data-testid="support-reply-input"
                rows={4}
                className="input-vault w-full"
                placeholder="Type your reply — customer receives it by email immediately."
                value={reply}
                onChange={e=>setReply(e.target.value)}
              />
              <button data-testid="support-reply-send" disabled={busy || !reply.trim()} onClick={sendReply} className="btn-gold w-full text-sm">
                {busy ? "Sending…" : "Send Reply + Email Customer"}
              </button>
              <div className="grid grid-cols-2 gap-2 pt-2">
                <button data-testid="support-mark-resolved" onClick={()=>setStatus(selected.id, "RESOLVED")} className="btn-outline-gold text-xs">Mark Resolved</button>
                <button data-testid="support-mark-closed" onClick={()=>setStatus(selected.id, "CLOSED")} className="btn-outline-gold text-xs">Close Ticket</button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get("/admin/audit-log").then(r=>setRows(r.data)); },[]);
  return (<Card title="Audit Log"><table className="vault-table"><thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Reason</th></tr></thead><tbody>{rows.map(r=>(<tr key={r.id}><td className="text-xs">{r.at?.slice(0,16).replace("T"," ")}</td><td className="font-mono text-xs">{r.actor_id}</td><td>{r.action}</td><td className="text-xs">{r.reason}</td></tr>))}</tbody></table></Card>);
}

/**
 * Admin → Referrals — full audit trail of every referral-code redemption
 * plus a one-click "Backfill" button that reruns the crediting logic for
 * any order that finished fulfilment but whose referral is still PENDING
 * (e.g. supplier webhook auto-completed the order but the customer never
 * clicked "I've received", so the historic delivery-confirm path never
 * fired). Idempotent — safe to click as many times as you like.
 */
function ReferralsAdmin() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/referrals/audit").then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  const backfill = async () => {
    if (!window.confirm("Re-run referral crediting for every FINISHED order whose referral is still PENDING.\n\nNOTE: this OVERRIDES the manual-confirm policy — it credits referrers on COMPLETED orders even if the customer hasn't clicked 'I've received the order' yet. Use this to clear historic leaks or after a supplier-webhook outage.\n\nSafe to run repeatedly (idempotent)."))
      return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/referrals/backfill");
      toast.success(`Scanned ${r.scanned} pending — credited ${r.credited} (skipped ${r.skipped_not_terminal} not-yet-fulfilled)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
    setBusy(false);
  };
  if (!data) return <Card title="Referrals"><div className="text-xs text-[var(--av-text-dim)]">Loading…</div></Card>;
  const rows = filter === "leaks" ? data.rows.filter(r => r.is_leak)
             : filter === "successful" ? data.rows.filter(r => r.redemption_status === "SUCCESSFUL")
             : filter === "pending" ? data.rows.filter(r => r.redemption_status === "PENDING")
             : filter === "reversed" ? data.rows.filter(r => ["REJECTED","REVERSED"].includes(r.redemption_status))
             : data.rows;
  const totalCredited = data.rows.filter(r => r.redemption_status === "SUCCESSFUL")
                                  .reduce((s, r) => s + r.actual_credited_gbp, 0);
  return (<Card title={<span className="flex justify-between items-center gap-2 flex-wrap">Referrals
    <span className="flex gap-2 flex-wrap items-center">
      <span className="text-xs text-[var(--av-text-dim)]">
        {data.total} total · <span className={data.leaks > 0 ? "text-red-400 font-semibold" : "text-emerald-400"}>{data.leaks} leaks</span> · £{totalCredited.toFixed(2)} credited
      </span>
      <button data-testid="referral-backfill-btn" onClick={backfill} disabled={busy} className="btn-outline-gold text-xs disabled:opacity-60">
        {busy ? "Crediting…" : "Backfill Referrals"}
      </button>
    </span>
  </span>}>
    <div className="flex gap-2 mb-3 flex-wrap">
      {[["all","All"],["leaks","Leaks only"],["pending","Pending"],["successful","Successful"],["reversed","Reversed"]].map(([k,l]) => (
        <button key={k} onClick={()=>setFilter(k)} data-testid={`referral-filter-${k}`}
          className={`text-xs px-3 py-1 rounded border ${filter===k?"bg-[var(--av-gold)] text-black border-[var(--av-gold)]":"border-[var(--av-gold)]/30 text-[var(--av-text-dim)]"}`}>
          {l}
        </button>
      ))}
    </div>
    <div className="overflow-x-auto">
      <table className="vault-table">
        <thead><tr>
          <th>Order</th><th>Status</th><th>Code</th>
          <th>Referrer</th><th>Referred Customer</th>
          <th>Expected</th><th>Credited</th><th>Applied</th><th>Leak?</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.redemption_id} data-testid={`referral-row-${r.redemption_id}`}
                className={r.is_leak ? "bg-red-500/5" : ""}>
              <td className="font-mono text-xs">
                <div>{r.order_id}</div>
                <div className="text-[10px] text-[var(--av-text-dim)]">{r.order_status}</div>
              </td>
              <td><span className={`pill ${r.redemption_status === "SUCCESSFUL" ? "pill-gold" : r.redemption_status === "PENDING" ? "pill-purple" : "pill-gold"}`}>{r.redemption_status}</span></td>
              <td className="font-mono text-xs">{r.referral_code}</td>
              <td className="text-xs">
                <div>{r.referrer_name || "—"}</div>
                <div className="text-[10px] text-[var(--av-text-dim)]">{r.referrer_email}</div>
                <div className="text-[10px] font-mono text-[var(--av-gold)]">{r.referrer_code}</div>
              </td>
              <td className="text-xs">
                <div className="text-[10px] font-mono text-[var(--av-text-dim)]">{r.referred_id}</div>
                <div className="text-[10px]">{r.referred_email}</div>
              </td>
              <td>£{r.expected_reward_gbp.toFixed(2)}</td>
              <td className={r.credit_gap_gbp > 0.01 ? "text-red-400 font-semibold" : "text-emerald-400"}>
                £{r.actual_credited_gbp.toFixed(2)}
              </td>
              <td className="text-[10px]">{r.applied_at?.slice(0,10)}</td>
              <td>{r.is_leak && <span className="pill pill-purple">LEAK</span>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="text-center text-xs text-[var(--av-text-dim)] py-6">No rows match this filter.</td></tr>}
        </tbody>
      </table>
    </div>
  </Card>);
}

function ReviewsAdmin() {  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const load = () => api.get("/admin/reviews").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const doDelete = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review removed");
      setConfirmFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove review");
    }
    setBusy(null);
  };
  const doBackfill = async () => {
    if (!window.confirm("Rewrite every existing review's author to the in-game name from its order? Safe to re-run.")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/reviews/backfill-in-game-names");
      toast.success(`Backfill done — ${data.updated} of ${data.total} reviews updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
    setBackfilling(false);
  };
  const openReply = (rev) => {
    setReplyFor(rev.id);
    setReplyBody(rev.owner_reply || "");
  };
  const submitReply = async ({ clear = false } = {}) => {
    if (!replyFor) return;
    if (!clear && !replyBody.trim()) {
      toast.error("Type a reply first, or use Clear to remove one.");
      return;
    }
    setReplyBusy(true);
    try {
      await api.post(`/admin/reviews/${replyFor}/reply`,
        { body: clear ? "" : replyBody.trim() });
      toast.success(clear ? "Reply cleared" : "Reply posted");
      setReplyFor(null); setReplyBody("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save reply");
    }
    setReplyBusy(false);
  };
  const target = confirmFor ? rows.find(r => r.id === confirmFor) : null;
  const replyTarget = replyFor ? rows.find(r => r.id === replyFor) : null;
  return (
    <>
      <Card title={`Reviews (${rows.length})`}>
        <div className="flex justify-end mb-3 -mt-1">
          <button
            data-testid="admin-review-backfill"
            onClick={doBackfill}
            disabled={backfilling || rows.length === 0}
            className="btn-outline-gold text-xs disabled:opacity-50"
          >{backfilling ? "Refreshing…" : "Refresh names from in-game"}</button>
        </div>
        <table className="vault-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Customer</th>
              <th>Rating</th>
              <th>Title</th>
              <th>Body</th>
              <th>Order</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`admin-review-row-${r.id}`}>
                <td className="text-xs whitespace-nowrap">{r.created_at?.slice(0,16).replace("T"," ")}</td>
                <td className="text-xs">
                  <div>{r.customer_name}</div>
                  <div className="text-[10px] text-[var(--av-text-dim)] font-mono">{r.customer_email}</div>
                </td>
                <td className="text-[var(--av-gold)] whitespace-nowrap">
                  {"★".repeat(r.rating)}<span className="text-[var(--av-text-muted)]">{"★".repeat(5 - r.rating)}</span>
                </td>
                <td className="text-sm font-semibold">{r.title}</td>
                <td className="text-xs max-w-sm">
                  <div className="line-clamp-3 text-[var(--av-text-dim)]">{r.body}</div>
                  {r.owner_reply && (
                    <div className="mt-1 pt-1 border-t border-[var(--av-gold)]/20 text-[11px] text-[var(--av-gold)]/90 line-clamp-2">
                      ↳ {r.owner_reply}
                    </div>
                  )}
                </td>
                <td className="font-mono text-[11px]">{r.order_id}</td>
                <td className="whitespace-nowrap space-y-1">
                  <div>
                    <button
                      data-testid={`admin-review-reply-${r.id}`}
                      onClick={() => openReply(r)}
                      className="inline-flex items-center gap-1 text-xs text-[var(--av-gold)] hover:underline"
                    >↳ {r.owner_reply ? "Edit reply" : "Reply"}</button>
                  </div>
                  <div>
                    <button
                      data-testid={`admin-review-delete-${r.id}`}
                      onClick={() => setConfirmFor(r.id)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 hover:underline"
                    >
                      <Trash2 className="w-3.5 h-3.5"/> Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="7" className="text-center text-[var(--av-text-dim)] py-6">No reviews yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      {target && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => busy == null && setConfirmFor(null)}>
          <div className="vault-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="mono-caption mb-2 text-red-400">Remove review</div>
            <h3 className="font-serif text-2xl mb-2">{target.title}</h3>
            <p className="text-xs text-[var(--av-text-dim)] mb-4">
              By <span className="text-[var(--av-gold)]">{target.customer_email || target.customer_name}</span> · Order <span className="font-mono">{target.order_id}</span>
            </p>
            <p className="text-sm text-[var(--av-text-dim)] mb-4">
              This permanently deletes the review. The customer can post a new one for the same order — verified-purchase gate still applies.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmFor(null)} disabled={busy === target.id} className="btn-outline-gold text-sm flex-1">Keep review</button>
              <button
                data-testid="admin-review-delete-confirm"
                onClick={() => doDelete(target.id)}
                disabled={busy === target.id}
                className="text-sm flex-1 px-4 py-2 rounded-full bg-red-500/90 hover:bg-red-500 text-white font-semibold transition disabled:opacity-60"
              >{busy === target.id ? "Removing…" : "Yes, remove"}</button>
            </div>
          </div>
        </div>
      )}
      {replyTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => !replyBusy && setReplyFor(null)}>
          <div className="vault-card p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="mono-caption gold-text mb-2">Reply publicly</div>
            <h3 className="font-serif text-xl mb-1">{replyTarget.title}</h3>
            <p className="text-xs text-[var(--av-text-dim)] mb-2">By {replyTarget.customer_name} · {"★".repeat(replyTarget.rating)}</p>
            <p className="text-xs text-[var(--av-text-dim)] italic mb-4 max-h-24 overflow-auto">"{replyTarget.body}"</p>
            <label className="text-xs block mb-4">Your reply (shown on the public Reviews page)
              <textarea
                data-testid="admin-review-reply-body"
                rows={5}
                className="input-vault mt-1"
                placeholder="Thanks for the honest feedback — here's what we did about it…"
                maxLength={2000}
                value={replyBody}
                onChange={e => setReplyBody(e.target.value)}
              />
              <span className="text-[10px] text-[var(--av-text-dim)] block text-right mt-1">{replyBody.length}/2000</span>
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setReplyFor(null)}
                disabled={replyBusy}
                className="btn-outline-gold text-sm flex-1"
              >Cancel</button>
              {replyTarget.owner_reply && (
                <button
                  type="button"
                  data-testid="admin-review-reply-clear"
                  onClick={() => submitReply({ clear: true })}
                  disabled={replyBusy}
                  className="text-sm px-4 py-2 rounded-full border border-red-400/60 text-red-400 hover:bg-red-500/10 disabled:opacity-60"
                >Clear reply</button>
              )}
              <button
                type="button"
                data-testid="admin-review-reply-submit"
                onClick={() => submitReply()}
                disabled={replyBusy || !replyBody.trim()}
                className="btn-gold text-sm flex-1"
              >{replyBusy ? "Posting…" : (replyTarget.owner_reply ? "Update reply" : "Post reply")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/**
 * EmailsAdmin — owner-managed template library + broadcast console.
 *
 * Left column: template list (create / edit / delete) — every template
 *   stores just `subject` + `html_body`; the send-time server wraps
 *   the body in the brand-safe email shell so drafts don't need to
 *   include header/footer HTML.
 * Right column: compose form — subject + HTML body, either loaded
 *   from a template or written from scratch. Recipient is either a
 *   single email or every eligible customer (verified + opt-in +
 *   non-example email). The `{{GREETING}}` placeholder in subject or
 *   body is substituted per recipient with the same in-game-name
 *   rule the transactional templates use.
 */
function EmailsAdmin() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null); // template being edited, or null
  const [tplName, setTplName] = useState("");
  const [tplSubject, setTplSubject] = useState("");
  const [tplBody, setTplBody] = useState("");
  const [tplBusy, setTplBusy] = useState(false);

  // Compose form
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("specific");
  const [specificEmail, setSpecificEmail] = useState("");
  const [personalize, setPersonalize] = useState(true);
  const [audience, setAudience] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadTemplates = () => api.get("/admin/email-templates").then(r => setTemplates(r.data));
  const loadAudience = () => api.get("/admin/email/audience-count").then(r => setAudience(r.data.count));
  useEffect(() => { loadTemplates(); loadAudience(); }, []);

  const openBlankTemplate = () => {
    setSelected("__new__"); setTplName(""); setTplSubject(""); setTplBody("");
  };
  const openTemplate = (t) => {
    setSelected(t.id); setTplName(t.name); setTplSubject(t.subject); setTplBody(t.html_body);
  };
  const closeTemplate = () => { setSelected(null); setTplName(""); setTplSubject(""); setTplBody(""); };
  const saveTemplate = async () => {
    if (!tplName.trim() || !tplSubject.trim() || !tplBody.trim()) {
      toast.error("Name, subject and body are all required"); return;
    }
    setTplBusy(true);
    try {
      const payload = { name: tplName.trim(), subject: tplSubject.trim(), html_body: tplBody };
      if (selected === "__new__") {
        await api.post("/admin/email-templates", payload);
        toast.success("Template created");
      } else {
        await api.put(`/admin/email-templates/${selected}`, payload);
        toast.success("Template updated");
      }
      await loadTemplates();
      closeTemplate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    }
    setTplBusy(false);
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/email-templates/${t.id}`);
      toast.success("Template deleted");
      await loadTemplates();
      if (selected === t.id) closeTemplate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete template");
    }
  };
  const loadIntoCompose = (t) => {
    setSubject(t.subject); setBody(t.html_body);
    toast.success(`Loaded "${t.name}" into compose`);
  };

  const sendEmail = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required"); return; }
    if (target === "specific" && !specificEmail.trim()) { toast.error("Enter a recipient email"); return; }
    if (target === "all" && !confirmSend) {
      // Two-step confirmation for the "all customers" broadcast to
      // avoid accidental mass sends.
      setConfirmSend(true);
      return;
    }
    setSending(true); setLastResult(null);
    try {
      const { data } = await api.post("/admin/email/send", {
        subject: subject.trim(),
        html_body: body,
        target,
        email: target === "specific" ? specificEmail.trim() : null,
        personalize_greeting: personalize,
      });
      setLastResult(data);
      toast.success(`Sent OK: ${data.sent} · Failed: ${data.failed}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSending(false);
    setConfirmSend(false);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-4">
      {/* LEFT: template library */}
      <div className="lg:col-span-2 min-w-0">
        <div className="vault-card p-4 sm:p-6 min-w-0">
          <div className="mono-caption mb-3">Email Templates</div>
          <div className="flex justify-between items-center mb-3 gap-2">
            <div className="text-xs text-[var(--av-text-dim)]">
              {templates.length} template{templates.length === 1 ? "" : "s"}
            </div>
            <button data-testid="new-template-btn" onClick={openBlankTemplate} className="btn-gold text-xs px-3 py-1">+ New template</button>
          </div>
          {templates.length === 0 && (
            <div className="text-sm text-[var(--av-text-dim)] py-6 text-center">No templates yet.</div>
          )}
          <ul className="space-y-2">
            {templates.map(t => (
              <li key={t.id} data-testid={`template-row-${t.id}`}
                  className={`rounded-lg border p-3 min-w-0 ${selected === t.id ? "border-[var(--av-gold)] bg-[rgba(212,175,55,0.06)]" : "border-[var(--av-border)]"}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm break-words">{t.name}</div>
                  <div className="text-[11px] text-[var(--av-text-dim)] break-words">{t.subject}</div>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button data-testid={`template-load-${t.id}`} onClick={() => loadIntoCompose(t)} className="text-[11px] px-2 py-1 rounded border border-[var(--av-purple)] text-[var(--av-purple)]">Load</button>
                  <button data-testid={`template-edit-${t.id}`} onClick={() => openTemplate(t)} className="text-[11px] px-2 py-1 rounded border border-[var(--av-gold)] text-[var(--av-gold)]">Edit</button>
                  <button data-testid={`template-delete-${t.id}`} onClick={() => deleteTemplate(t)} className="text-[11px] px-2 py-1 rounded border border-red-500/40 text-red-300">Delete</button>
                </div>
              </li>
            ))}
          </ul>
          {selected && (
            <div className="mt-4 p-3 rounded-lg border border-[var(--av-gold)]/40 bg-[rgba(212,175,55,0.05)] min-w-0">
              <div className="mono-caption mb-2">{selected === "__new__" ? "New Template" : "Edit Template"}</div>
              <input data-testid="tpl-name" className="input-vault mb-2" placeholder="Template name (e.g. Special Offers Launch)"
                     value={tplName} onChange={e => setTplName(e.target.value)}/>
              <input data-testid="tpl-subject" className="input-vault mb-2" placeholder="Subject (supports {{GREETING}})"
                     value={tplSubject} onChange={e => setTplSubject(e.target.value)}/>
              <textarea data-testid="tpl-body" className="input-vault mb-2 font-mono text-xs w-full" rows={10}
                        placeholder="HTML body (use {{GREETING}} for the customer's in-game name)"
                        value={tplBody} onChange={e => setTplBody(e.target.value)}/>
              <div className="flex gap-2 flex-wrap">
                <button data-testid="tpl-save" onClick={saveTemplate} disabled={tplBusy} className="btn-gold text-xs px-3 py-1">
                  {tplBusy ? "Saving…" : "Save"}
                </button>
                <button onClick={closeTemplate} className="text-xs px-3 py-1 rounded border border-[var(--av-border)]">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: compose + send */}
      <div className="lg:col-span-3 min-w-0">
        <div className="vault-card p-4 sm:p-6 min-w-0">
          <div className="mono-caption mb-3">Send Email</div>
          <div className="space-y-3">
            <div>
              <label className="mono-caption block mb-1">Subject</label>
              <input data-testid="compose-subject" className="input-vault" placeholder="Subject (supports {{GREETING}})"
                     value={subject} onChange={e => setSubject(e.target.value)}/>
            </div>
            <div>
              <label className="mono-caption block mb-1">HTML Body</label>
              <textarea data-testid="compose-body" className="input-vault font-mono text-xs w-full" rows={14}
                        placeholder="HTML content — use {{GREETING}} where the customer's name should appear."
                        value={body} onChange={e => setBody(e.target.value)}/>
              <div className="text-[10px] text-[var(--av-text-dim)] mt-1">
                Tip: use <span className="font-mono text-[var(--av-gold)]">{"{{GREETING}}"}</span> anywhere and it'll be swapped for each recipient's default game-profile in-game name. Body is wrapped server-side in the brand-safe email shell — you don't need to add your own header or footer.
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className={`rounded-lg border p-3 cursor-pointer transition min-w-0 ${target === "specific" ? "border-[var(--av-gold)] bg-[rgba(212,175,55,0.06)]" : "border-[var(--av-border)]"}`}>
                <div className="flex items-center gap-2">
                  <input data-testid="target-specific" type="radio" name="target" value="specific" checked={target === "specific"} onChange={() => { setTarget("specific"); setConfirmSend(false); }}/>
                  <span className="font-semibold text-sm">Specific email</span>
                </div>
                <div className="text-[11px] text-[var(--av-text-dim)] mt-1">Send to a single address. Good for tests and one-offs.</div>
              </label>
              <label className={`rounded-lg border p-3 cursor-pointer transition min-w-0 ${target === "all" ? "border-[var(--av-purple)] bg-[rgba(148,0,211,0.06)]" : "border-[var(--av-border)]"}`}>
                <div className="flex items-center gap-2">
                  <input data-testid="target-all" type="radio" name="target" value="all" checked={target === "all"} onChange={() => { setTarget("all"); setConfirmSend(false); }}/>
                  <span className="font-semibold text-sm">All customers</span>
                </div>
                <div className="text-[11px] text-[var(--av-text-dim)] mt-1">
                  Sends to every customer email on file ({audience ?? "…"} recipients).
                </div>
              </label>
            </div>

            {target === "specific" && (
              <div>
                <label className="mono-caption block mb-1">Recipient email</label>
                <input data-testid="specific-email" className="input-vault" placeholder="customer@example.com"
                       value={specificEmail} onChange={e => setSpecificEmail(e.target.value)}/>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" data-testid="personalize-checkbox" checked={personalize} onChange={e => setPersonalize(e.target.checked)} className="mt-1"/>
              <span>Personalise greeting — replace <span className="font-mono text-[var(--av-gold)]">{"{{GREETING}}"}</span> with each recipient's in-game name.</span>
            </label>

            {target === "all" && confirmSend && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-sm">
                <div className="font-semibold text-red-300 mb-1">Confirm broadcast</div>
                <div className="text-[var(--av-text-dim)] text-xs">You're about to send this email to <b>{audience}</b> customer{audience === 1 ? "" : "s"}. Click Send again to confirm, or change the recipient to cancel.</div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <button data-testid="send-email-btn" onClick={sendEmail} disabled={sending}
                      className={target === "all" ? "btn-purple" : "btn-gold"}>
                {sending ? "Sending…" : (target === "all" ? (confirmSend ? `Confirm — send to ${audience}` : "Send to all customers") : "Send")}
              </button>
              <button type="button" onClick={() => { setSubject(""); setBody(""); setLastResult(null); }} className="text-xs px-3 py-2 rounded border border-[var(--av-border)]">Clear form</button>
            </div>

            {lastResult && (
              <div data-testid="send-result" className="mt-4 rounded-lg border border-[var(--av-gold)]/40 bg-[rgba(212,175,55,0.05)] p-3 min-w-0">
                <div className="mono-caption mb-2">Send Result</div>
                <div className="text-sm mb-2">
                  <span className="text-[var(--av-gold)] font-semibold">Sent OK: {lastResult.sent}</span>
                  <span className="mx-2">·</span>
                  <span className={lastResult.failed > 0 ? "text-red-300 font-semibold" : "text-[var(--av-text-dim)]"}>Failed: {lastResult.failed}</span>
                </div>
                <div className="max-h-64 overflow-y-auto text-[11px] space-y-1 font-mono">
                  {(lastResult.results || []).map((row, i) => (
                    <div key={row.msg_id || row.email || i} className={`p-2 rounded break-all ${row.error ? "bg-red-500/10 text-red-200" : "bg-[rgba(148,0,211,0.06)] text-[var(--av-text)]"}`}>
                      <div><b>{row.email}</b> as "{row.greeting}"</div>
                      <div className="text-[10px] opacity-80">{row.error ? `ERROR: ${row.error}` : `msg=${row.msg_id}`}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
